/**
 * Set Configurator - Web Component für Starter Sets
 * Lazy-loaded und nur auf Set-Produktseiten aktiv
 */

const GDN_SLOT_COUNT_PATTERNS = [
  /(\d+)\s*Farbe/i,
  /(\d+)\s*Top\s*Coats?/i,
];

// "Optionale Farbe": Zusatzfarben nach dem Pflicht-Set, rabattiert gegenüber
// dem Einzelpreis. Den Preis trägt die Set-Variante über die Produktoption
// "Anzahl" (siehe _syncColorCountOption) - dieser Wert dient nur noch dem
// Badge auf den Bonus-Slots und muss zur Preisstaffel am Produkt passen.
//
// Nach dem Sale (Sonntag) einschalten, einzeln oder beide:
//   GDN_OPTIONAL_COLORS_ENABLED = true  (oder Metafeld custom.enable_optional_colors)
//   GDN_ATC_PRICE_ENABLED = true        (Preis im „In den Warenkorb“-Button)
// Optionale Farben gelten nur für die Handles unten (Essentials), nicht Starter-Set.
const GDN_OPTIONAL_COLOR_DISCOUNT = 0.2;
const GDN_OPTIONAL_COLORS_ENABLED = false;
const GDN_ATC_PRICE_ENABLED = false;
const GDN_OPTIONAL_COLORS_HANDLES = [
  "no-file-gel-essentials-set",
  "no-file-gel-essentials-set-testset-fur-entwickler-nicht-kaufen",
];
const GDN_MAX_OPTIONAL_COLORS = 10;
const GDN_OPTIONAL_GROUP_PATTERN = /optionale?\s+farbe/i;
const GDN_OPTIONAL_SLOT_LABEL = "Optionale Farbe";

function gdnParseSlotCountFromText(text) {
  if (!text) return null;
  const value = String(text);
  for (const pattern of GDN_SLOT_COUNT_PATTERNS) {
    const match = value.match(pattern);
    if (match) {
      return parseInt(match[1], 10);
    }
  }
  return null;
}

class SetConfigurator extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.fuseLoaded = false;
    this.observer = null;
    this.priceObserverInterval = null;
    this._pageScrollLocked = false;
    this._lockedScrollY = 0;
    this.activeColorCount = null;
  }

  async connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    // Metafield-Daten aus data-Attributen holen
    this.colorOptions = this.dataset.colorOptions || "";
    this.customFields = this.dataset.customFields || "";
    this.productId = this.dataset.productId || "";
    this.productHandle = (this.dataset.productHandle || "").toLowerCase();

    // Prüfen ob color_options vorhanden
    if (
      !this.colorOptions ||
      this.colorOptions === "" ||
      this.colorOptions === "null"
    ) {
      this.handleNoColorOptions();
      return;
    }

    try {
      this.parsedColorOptions = JSON.parse(this.colorOptions);
      this.parsedCustomFields = this.customFields
        ? JSON.parse(this.customFields)
        : null;
    } catch (e) {
      console.warn("SetConfigurator: Invalid JSON data", e);
      this.handleNoColorOptions();
      return;
    }

    // Event-basierte Initialisierung statt Polling
    await this.waitForHulkApps();
    await this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  // Hilfsfunktion: Entfernt "[ €0.0 ]", "[ €12.50 ]" etc. aus Texten
  cleanPriceText(text) {
    if (!text) return "";
    return text.replace(/\s*\[\s*€[\d.,]+\s*\]/g, "").trim();
  }

  normalizeDrawerTitle(text) {
    if (!text) return "";
    return this.cleanPriceText(text).replace(/\s*\(\d+\)\s*$/g, "").trim();
  }

  getDrawerHeaderTitle() {
    const fromMeta =
      this.parsedCustomFields?.fields?.side_drawer_content
        ?.side_drawer_top_section_title;
    return this.normalizeDrawerTitle(fromMeta || "");
  }

  getDrawerTitleEl() {
    return this.querySelector("#side-drawer .drawer__header .drawer__title");
  }

  setDrawerHeaderTitle(text) {
    const imgContainer = this.getDrawerTitleEl();
    if (!imgContainer) return;
    imgContainer.textContent = this.normalizeDrawerTitle(text);
  }

  /**
   * Zeigt die passende Hulk-Optionsgruppe und setzt den Drawer-Header
   * für den angeklickten Slot (leer oder bereits gefüllt / Austausch).
   */
  _syncDrawerForOption(optionEl, { retry = true } = {}) {
    if (!optionEl) return;

    const filteredClass = Array.from(optionEl.classList).find((cls) =>
      cls.startsWith("option-")
    );
    if (!filteredClass) return;

    const parentHulk = document.querySelector(".hulkapps_option_set");
    if (!parentHulk) {
      this.setDrawerHeaderTitle(this.getDrawerHeaderTitle());
      return;
    }

    const optionClasses = parentHulk.querySelectorAll(
      "div.hulkapps_option.swatch_render"
    );
    let headerSet = false;

    optionClasses.forEach((opt) => {
      const hulkOption = Array.from(opt.classList).find((c) =>
        c.startsWith("option-")
      );
      const isMatch = filteredClass === hulkOption;
      opt.style.display = isMatch ? "block" : "none";

      if (isMatch) {
        const imgContainer = this.getDrawerTitleEl();
        imgContainer
          ?.querySelectorAll("span")
          .forEach((span) => span.remove());
        this.setDrawerHeaderTitle(this._resolvePickerDrawerTitle());
        headerSet = true;
      }
    });

    if (!headerSet) {
      if (retry) {
        setTimeout(
          () => this._syncDrawerForOption(optionEl, { retry: false }),
          300
        );
      } else {
        this.setDrawerHeaderTitle(this.getDrawerHeaderTitle());
      }
    }
  }

  _resetDrawerScroll() {
    const sideDrawer = this.querySelector("#side-drawer");
    if (sideDrawer) {
      sideDrawer.scrollTop = 0;
      sideDrawer.querySelector(".set-drawer__body")?.scrollTo(0, 0);
    }
  }

  _getServerSlots() {
    return Array.from(
      this.querySelectorAll(
        ".custom-options-enterprise .option:not([data-new-container])"
      )
    );
  }

  _getFirstEmptyServerSlot() {
    return this._getServerSlots().find((slot) => !this._isSlotFilled(slot));
  }

  _isSlotFilled(slotEl) {
    if (slotEl?.querySelector(".design img")) return true;

    const p = slotEl?.querySelector(":scope > p");
    if (!p?.textContent) return false;

    const text = this.cleanPriceText(p.textContent);
    const original = (p.getAttribute("data-original-text") || "").trim();
    return !!(text && text !== original);
  }

  _getFilledServerSlotCount() {
    return this._getServerSlots().filter((slot) => this._isSlotFilled(slot))
      .length;
  }

  _getRequiredSlotCount() {
    const fromActive = this.activeColorCount;
    if (fromActive !== null && fromActive > 0) return fromActive;
    const slots = this._getServerSlots();
    return slots.length > 0 ? slots.length : 0;
  }

  /**
   * Ob noch eine weitere Farbe passt - inklusive Bonus-Slots, damit der
   * Plus-Button im Swatch-Overlay auch Zusatzfarben hinzufügen kann.
   */
  _canAddMoreSelections() {
    return !!this._getFirstEmptyTargetSlot();
  }

  _getOptionalSlots() {
    return Array.from(
      this.querySelectorAll(
        '.custom-options-enterprise .option[data-new-container="true"]'
      )
    );
  }

  /**
   * Pflicht- und Bonus-Slots in DOM-Reihenfolge. Für alles, was die tatsächlich
   * gewählten Farben betrifft (Mengen-Overlays, Drawer-Chooser) - im Gegensatz
   * zu _getServerSlots(), das nur die Pflicht-Slots für Fortschritt und
   * Add-to-Cart-Freigabe liefert.
   */
  _getAllSelectableSlots() {
    return Array.from(
      this.querySelectorAll(".custom-options-enterprise .option")
    );
  }

  _getFilledOptionalSlots() {
    return this._getOptionalSlots().filter((slot) => this._isSlotFilled(slot));
  }

  _areAllRequiredSlotsFilled() {
    const required = this._getRequiredSlotCount();
    return required > 0 && this._getFilledServerSlotCount() >= required;
  }

  /**
   * Erster Slot, den die nächste Swatch-Auswahl füllen soll: immer zuerst die
   * Pflicht-Slots, erst danach ein leerer Bonus-Slot. Diese Reihenfolge ist
   * der Grund, warum die Bonus-Slots früher den Auswahl-Pointer "gestohlen"
   * haben - ohne sie kann der User vorhandene Farben nicht mehr austauschen.
   */
  _getFirstEmptyTargetSlot() {
    return (
      this._getFirstEmptyServerSlot() ||
      this._getOptionalSlots().find((slot) => !this._isSlotFilled(slot)) ||
      null
    );
  }

  _getAllHulkGroups() {
    return Array.from(
      document.querySelectorAll(
        ".hulkapps_option_set .hulkapps_option.swatch_render"
      )
    );
  }

  /**
   * Gruppentitel aus dem Hulk-DOM. Bei euch steht der Name oft nicht als
   * sichtbares Label, sondern nur im name der Hidden-Property
   * (z. B. properties[Teil deines No File Sets (1)]).
   */
  _getHulkGroupTitle(group) {
    if (!group) return "";

    const fromData = group.dataset?.optionName || group.dataset?.label || "";
    if (fromData) return fromData.trim();

    const hiddenPropName = group.querySelector(".hulk_swatch_hidden_prop")?.name || "";
    const fromHiddenProp = hiddenPropName.match(/properties\[(.+)\]/)?.[1];
    if (fromHiddenProp) return fromHiddenProp.trim();

    const labelNode = group.querySelector(
      ".hulkapps_option_label, .hulkapps_option_title, .hulkapps_label, .hulk_option_label, legend, :scope > label"
    );
    return (labelNode?.textContent || "").trim();
  }

  /**
   * Anzahl der Pflicht-Farben im größten Set-Variant. Die optionalen Gruppen
   * liegen im Hulk-Backend dahinter - nur so lassen sie sich per Index
   * abgrenzen, wenn der Gruppentitel nicht auslesbar ist.
   */
  _getMaxRequiredColorCount() {
    const fromMetafield = this.parsedColorOptions?.options?.length || 0;
    if (fromMetafield > 0) return fromMetafield;
    if (this.activeColorCount > 0) return this.activeColorCount;
    return this._getServerSlots().length;
  }

  _optionalColorsEnabled() {
    if (!GDN_OPTIONAL_COLORS_HANDLES.includes(this.productHandle)) {
      return false;
    }
    if (this.dataset.optionalColors === "true") return true;
    return GDN_OPTIONAL_COLORS_ENABLED;
  }

  _canOfferOptionalColors() {
    if (!this._optionalColorsEnabled()) return false;
    if (!this._areAllRequiredSlotsFilled()) return false;
    return this._getFilledOptionalSlots().length < this._getOptionalCapacity();
  }

  _resolveOptionalHulkGroups() {
    if (!this._optionalColorsEnabled()) return [];

    const groups = this._getAllHulkGroups();

    const byTitle = groups.filter((group) =>
      GDN_OPTIONAL_GROUP_PATTERN.test(this._getHulkGroupTitle(group))
    );
    if (byTitle.length) return byTitle.slice(0, GDN_MAX_OPTIONAL_COLORS);

    return groups
      .slice(this._getRequiredSlotCount())
      .slice(0, GDN_MAX_OPTIONAL_COLORS);
  }

  /**
   * Hulk vergibt option-N-Klassen nicht zuverlässig an neu angelegte Gruppen
   * (z. B. "Optionale Farbe" / TEST-Gruppen). Ohne diese Klasse finden
   * Bonus-Slot, Hidden-Property-Sync und Preisberechnung die Gruppe nicht.
   */
  _ensureHulkGroupSlotClasses() {
    this._getAllHulkGroups().forEach((group, index) => {
      if (this._getSlotClassFromElement(group)) return;
      group.classList.add(`option-${index + 1}`);
    });
  }

  /**
   * Markiert die optionalen Hulk-Gruppen einmalig per data-Attribut, damit sie
   * danach billig per Selektor abfragbar sind.
   */
  _markOptionalHulkGroups() {
    this._ensureHulkGroupSlotClasses();

    const optional = new Set(this._resolveOptionalHulkGroups());

    this._getAllHulkGroups().forEach((group) => {
      if (optional.has(group)) {
        group.dataset.gdnOptional = "true";
      } else {
        delete group.dataset.gdnOptional;
      }
    });
  }

  _getOptionalHulkGroups() {
    return Array.from(
      document.querySelectorAll(
        '.hulkapps_option_set .hulkapps_option.swatch_render[data-gdn-optional="true"]'
      )
    );
  }

  _getOptionalCapacity() {
    return Math.min(
      this._getOptionalHulkGroups().length,
      GDN_MAX_OPTIONAL_COLORS
    );
  }

  /**
   * Setzt die Produktoption "Anzahl" auf Pflichtfarben + gewählte Zusatzfarben.
   * Diese Option ist die Preisstufe des Sets: der Variant-Picker des Themes
   * rendert daraufhin Preis, Streichpreis und die versteckte Variant-ID neu.
   * Damit rechnet das Theme keine Preise mehr selbst - was angezeigt wird, ist
   * per Definition auch das, was im Checkout berechnet wird.
   */
  _syncColorCountOption() {
    const input = document.querySelector("input[data-gdn-color-count-option]");
    if (!input) return;

    let valueIds;
    try {
      valueIds = JSON.parse(input.dataset.gdnColorCountValues || "{}");
    } catch (e) {
      return;
    }

    const total =
      this._getRequiredSlotCount() + this._getFilledOptionalSlots().length;
    const targetId = valueIds[String(total)];

    // Fehlende Stufe heißt: für diese Farbanzahl gibt es keine Variante. Dann
    // die aktuelle Auswahl stehen lassen, statt auf eine falsche zu wechseln.
    if (!targetId || input.value === targetId) return;

    input.value = targetId;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  _getColorCountPriceMap() {
    if (this._colorCountPriceMap) return this._colorCountPriceMap;

    const map = {};
    const input = document.querySelector("[data-gdn-color-count-option]");
    try {
      Object.assign(
        map,
        JSON.parse(input?.dataset.gdnColorCountPrices || "{}")
      );
    } catch (e) {
      // Karte bleibt leer, Fallback über Varianten-JSON bzw. 20 %.
    }

    if (!Object.keys(map).length) {
      document
        .querySelectorAll("script[data-variant-detail-data]")
        .forEach((script) => {
          let variants = [];
          try {
            variants = JSON.parse(script.textContent);
          } catch (e) {
            return;
          }
          if (!Array.isArray(variants)) return;

          variants.forEach((variant) => {
            const opts = [variant.option1, variant.option2, variant.option3];
            let type = null;
            let count = null;
            opts.forEach((opt) => {
              if (opt == null || opt === "") return;
              const parsed =
                gdnParseSlotCountFromText(opt) ??
                (/^\d+$/.test(String(opt).trim())
                  ? parseInt(opt, 10)
                  : null);
              if (parsed != null) count = parsed;
              else if (!type) type = String(opt).trim();
            });
            if (count == null) {
              count = gdnParseSlotCountFromText(variant.count_label);
            }
            if (!type || !count) return;
            map[`${type}|${count}`] = {
              p: Number(variant.price),
              c: Number(variant.compare_at_price || variant.price),
            };
          });
        });
    }

    this._colorCountPriceMap = map;
    return map;
  }

  _getSelectedSetType() {
    if (this._selectedSetType) return this._selectedSetType;

    const input = document.querySelector("[data-gdn-color-count-option]");
    if (input?.dataset.gdnSetType) {
      this._selectedSetType = input.dataset.gdnSetType;
      return this._selectedSetType;
    }

    const firstKey = Object.keys(this._getColorCountPriceMap())[0] || "";
    return firstKey.split("|")[0] || "";
  }

  _getPriceEntry(type, count) {
    const map = this._getColorCountPriceMap();
    const exact = map[`${type}|${count}`] || map[`_|${count}`];
    if (exact) return exact;

    return (
      Object.entries(map).find(([key]) => {
        const [keyType, keyCount] = key.split("|");
        const parsed =
          gdnParseSlotCountFromText(keyCount) ??
          (/^\d+$/.test(String(keyCount || "").trim())
            ? parseInt(keyCount, 10)
            : null);
        return (
          parsed === count &&
          (!type || keyType === type || keyType === "_")
        );
      })?.[1] || null
    );
  }

  /**
   * Aufpreis einer einzelnen Zusatzfarbe als Differenz der Stufen
   * `totalCount - 1` → `totalCount` (Verkauf + Streichpreis).
   * Fehlt die Anzahl-Staffel, gilt −20 % auf 14,90 € (Einzelpreis Gel).
   */
  _getOptionalUnitPrices(totalCount) {
    const type = this._getSelectedSetType();
    const current = this._getPriceEntry(type, totalCount);
    const previous = this._getPriceEntry(type, totalCount - 1);

    if (current && previous) {
      const sale = current.p - previous.p;
      if (sale > 0) {
        let compare =
          (current.c || current.p) - (previous.c || previous.p);
        if (compare <= sale) {
          compare = Math.round(sale / (1 - GDN_OPTIONAL_COLOR_DISCOUNT));
        }
        const percent = Math.round(((compare - sale) * 100) / compare);
        return { sale, compare, percent };
      }
    }

    const compare = 1490;
    const sale = Math.round(compare * (1 - GDN_OPTIONAL_COLOR_DISCOUNT));
    return {
      sale,
      compare,
      percent: Math.round(GDN_OPTIONAL_COLOR_DISCOUNT * 100),
    };
  }

  _formatMoneyCents(cents) {
    const tv = window.themeVariables && window.themeVariables.settings;
    const format =
      tv && tv.currencyCodeEnabled
        ? tv.moneyWithCurrencyFormat
        : tv && tv.moneyFormat;

    if (typeof window.formatMoney === "function") {
      return window.formatMoney(cents, format || "");
    }

    const number = (Math.round(cents) / 100).toFixed(2).replace(".", ",");
    return `€ ${number}`;
  }

  _isOptionalSlot(slot) {
    return !!(
      slot?.hasAttribute("data-new-container") ||
      slot?.classList.contains("discount-farbe")
    );
  }

  _getOptionalPricesForSlot(slot) {
    const index = parseInt(slot?.getAttribute("data-count"), 10);
    if (!index) return null;
    return this._getOptionalUnitPrices(this._getRequiredSlotCount() + index);
  }

  _buildOptionalPriceRow(unit) {
    if (!unit) return null;

    const host = document.createElement("div");
    host.className = "gdn-optional-slot-price";

    const nowEl = document.createElement("span");
    nowEl.className = "gdn-optional-slot-price__now";
    nowEl.textContent = this._formatMoneyCents(unit.sale);
    host.appendChild(nowEl);

    if (unit.compare > unit.sale) {
      const compareEl = document.createElement("s");
      compareEl.className = "gdn-optional-slot-price__compare";
      compareEl.textContent = this._formatMoneyCents(unit.compare);
      host.appendChild(compareEl);
    }

    return host;
  }

  /**
   * Verkaufspreis + Streichpreis unter dem Namen einer bereits gewählten
   * Zusatzfarbe. Leere „Optionale Farbe“-Slots bleiben ohne Preiszeile;
   * den Rabatt zeigt dort nur das −%-Badge im Bild.
   */
  _syncOptionalSlotPrices() {
    this._getOptionalSlots().forEach((slot) => {
      const unit = this._getOptionalPricesForSlot(slot);
      let host = slot.querySelector(":scope > .gdn-optional-slot-price");

      if (unit?.percent > 0) {
        const slotBadge = slot.querySelector(":scope > .container-label");
        if (slotBadge) slotBadge.textContent = `-${unit.percent}%`;
      }

      if (!unit || !this._isSlotFilled(slot)) {
        host?.remove();
        return;
      }

      const row = this._buildOptionalPriceRow(unit);
      if (!row) {
        host?.remove();
        return;
      }

      if (host) host.replaceWith(row);
      else {
        const label = slot.querySelector(":scope > p");
        if (label) label.after(row);
        else slot.appendChild(row);
      }
    });
  }

  _getSlotClassFromElement(slotEl) {
    return Array.from(slotEl?.classList || []).find((cls) =>
      cls.startsWith("option-")
    );
  }

  _getColorQuantity(colorName) {
    const normalized = this.cleanPriceText(colorName || "");
    if (!normalized) return 0;

    return this._getAllSelectableSlots().filter((slot) => {
      return this._getOptionColorName(slot) === normalized;
    }).length;
  }

  _setDrawerFooterVisible(isVisible) {
    const footer = this.querySelector("#set-drawer-footer");
    if (!footer) return;

    if (isVisible) {
      footer.hidden = false;
      footer.removeAttribute("hidden");
      footer.classList.add("is-visible");
    } else {
      footer.hidden = true;
      footer.setAttribute("hidden", "");
      footer.classList.remove("is-visible");
    }
  }

  _syncDrawerFooter() {
    const sideDrawer = this.querySelector("#side-drawer");
    const drawerOpen = sideDrawer?.classList.contains("open");
    this._setDrawerFooterVisible(!!drawerOpen);

    if (drawerOpen) {
      this._syncDrawerChooser();
      this._updateDrawerConfirmButton();
    }

    this._syncSetProgressCopy();
  }

  _scheduleDrawerFooterSync() {
    [0, 100, 400, 800].forEach((delay) => {
      window.setTimeout(() => {
        const sideDrawer = this.querySelector("#side-drawer");
        if (sideDrawer?.classList.contains("open")) {
          this._syncDrawerFooter();
          this._syncSwatchQtyOverlays();
        }
      }, delay);
    });
  }

  _openDrawerForPicker() {
    const sideDrawer = this.querySelector("#side-drawer");
    if (!sideDrawer) return;

    sideDrawer.classList.add("open");
    this._syncDrawerScrollLock(true);
    this._syncBlurOverlay(true);
    this._resetDrawerScroll();

    // Schwere DOM-Arbeit erst nach dem ersten Paint — Drawer + Overlay sofort sichtbar
    requestAnimationFrame(() => {
      this.checkMatchingOpt();
      this.updateSelectionStatus();
      this.updateButtonVisibility();
      this._onDrawerOpenedForPicker();
    });
  }

  _updateDrawerFooterVisibility() {
    this._syncDrawerFooter();
  }

  _resolvePickerDrawerTitle() {
    const { pickerTitle } = this.getSlotConfig();
    return pickerTitle || "Wähle deine Farben";
  }

  _getSlotNoun(count) {
    const { slotSingular } = this.getSlotConfig();
    if (/top\s*coat/i.test(slotSingular)) {
      return count === 1 ? "Top Coat" : "Top Coats";
    }
    return count === 1 ? "Farbe" : "Farben";
  }

  _drawerCheckIconHtml() {
    return `<svg class="gdn-set-check" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }

  _getRemainingRequiredCount() {
    return Math.max(
      0,
      this._getRequiredSlotCount() - this._getFilledServerSlotCount()
    );
  }

  _fillProgressStatusEl(el) {
    if (!el) return;

    const required = this._getRequiredSlotCount();
    const remaining = this._getRemainingRequiredCount();
    if (required <= 0) {
      el.hidden = true;
      el.classList.remove("is-complete");
      el.textContent = "";
      return;
    }

    el.hidden = false;
    el.classList.toggle("is-complete", remaining <= 0);

    if (remaining > 0) {
      el.textContent = `Noch ${remaining} ${this._getSlotNoun(remaining)} wählen`;
      return;
    }

    if (this._canOfferOptionalColors()) {
      const nextCount = this._getRequiredSlotCount() + this._getFilledOptionalSlots().length + 1;
      const percent =
        this._getOptionalUnitPrices(nextCount)?.percent ||
        Math.round(GDN_OPTIONAL_COLOR_DISCOUNT * 100);
      el.innerHTML = `${this._drawerCheckIconHtml()}<strong>Set komplett.</strong> weitere Farben jetzt <span class="gdn-set-status-percent">−${percent}%</span>`;
      return;
    }

    el.innerHTML = `${this._drawerCheckIconHtml()}<strong>Set komplett</strong>`;
  }

  _getSelectedColorCount() {
    return this._getAllSelectableSlots().filter((slot) =>
      this._isSlotFilled(slot)
    ).length;
  }

  _selectedCountLabel(count) {
    return `${count} ${this._getSlotNoun(count)} ausgewählt`;
  }

  _fillCompleteHeadingEl(el) {
    if (!el) return;

    const complete = this._areAllRequiredSlotsFilled();
    el.hidden = false;
    el.classList.toggle("is-complete", complete);
    el.innerHTML = complete
      ? `${this._drawerCheckIconHtml()}<strong>Set komplett</strong>`
      : `<strong>${this._selectedCountLabel(this._getSelectedColorCount())}</strong>`;
  }

  _syncSetProgressCopy() {
    this._fillProgressStatusEl(this.querySelector("#set-drawer-status"));
    this._fillCompleteHeadingEl(
      this.querySelector("#set-drawer-chooser-heading")
    );
  }

  _updateDrawerPickerHeader() {
    this.setDrawerHeaderTitle(this._resolvePickerDrawerTitle());
    this._syncSetProgressCopy();
  }

  _showDrawerSwatchGrid() {
    const groups = document.querySelectorAll(
      "#side-drawer .hulkapps_option.swatch_render"
    );
    groups.forEach((opt, index) => {
      opt.style.display = index === 0 ? "block" : "none";
    });
    this._updateDrawerPickerHeader();
  }

  _findMatchingSwatchInGroup(hulkSet, sourceSwatch) {
    const { ariaLabelValue } = this._getSwatchSelectionData(sourceSwatch);
    if (!ariaLabelValue || !hulkSet) return sourceSwatch;

    const match = Array.from(
      hulkSet.querySelectorAll(".hulkapps_swatch_option")
    ).find((label) => {
      const labelName = this.cleanPriceText(
        label.dataset.swatchLabel ||
          label.getAttribute("aria-label") ||
          label.querySelector(".hulk_po_radio")?.dataset?.conditionalValue ||
          ""
      );
      return labelName === ariaLabelValue;
    });

    return match || sourceSwatch;
  }

  _commitHulkSwatchSelectionForSlot(sourceSwatch, slotClass) {
    if (!slotClass) {
      this._commitHulkSwatchSelection(sourceSwatch);
      return;
    }

    const hulkSet = document.querySelector(
      `.hulkapps_option.swatch_render.${slotClass}`
    );
    if (!hulkSet) {
      this._commitHulkSwatchSelection(sourceSwatch);
      return;
    }

    const targetSwatch = this._findMatchingSwatchInGroup(hulkSet, sourceSwatch);
    this._commitHulkSwatchSelection(targetSwatch);
  }

  _addSwatchToSlot(slotEl, swatchOption) {
    if (!slotEl || !swatchOption) return false;

    const { imgSrc, ariaLabelValue } =
      this._getSwatchSelectionData(swatchOption);
    const slotClass = this._getSlotClassFromElement(slotEl);

    const optionText = slotEl.querySelector(":scope > p");
    if (optionText && ariaLabelValue) {
      optionText.textContent = ariaLabelValue;
    }

    const designDiv = slotEl.querySelector(".design");
    if (designDiv && imgSrc) {
      designDiv.innerHTML = `<img src="${imgSrc}" style="width: 100%; height: auto;">`;
      designDiv.classList.add("hide-after");
      designDiv.style.backgroundColor = "white";
    }

    this._commitHulkSwatchSelectionForSlot(swatchOption, slotClass);

    if (ariaLabelValue) {
      this._lastSelectedColorName = ariaLabelValue;
    }

    this.updateBannerMessage();
    this.updateButtonVisibility();
    this.updateSelectionStatus();
    this.checkAndAppendContainers();
    this._advancePendingToNextEmpty();
    this.getCurrentActiveCont();
    this._syncSwatchQtyOverlays();
    this._updateDrawerPickerHeader();
    this._syncDrawerFooter();

    return true;
  }

  /**
   * Nach einer Auswahl zeigt der Drawer den Rahmen auf dem nächsten leeren
   * Slot (z. B. „Optionale Farbe“), nicht auf der gerade gefüllten Farbe.
   */
  _advancePendingToNextEmpty() {
    const nextEmpty = this._getFirstEmptyTargetSlot();
    this._pendingSlot = nextEmpty;
    this.querySelectorAll(".option").forEach((opt) =>
      opt.classList.remove("active")
    );
    if (nextEmpty) nextEmpty.classList.add("active");
  }

  _removeSlotSelection(slotEl) {
    if (!slotEl) return;

    this._clearSelectionForContainer(slotEl);
    this._lastSelectedColorName = null;

    this.updateBannerMessage();
    this.updateButtonVisibility();
    this.updateSelectionStatus();
    this._syncOptionalSlots();
    this._advancePendingToNextEmpty();
    this.getCurrentActiveCont();
    this._syncSwatchQtyOverlays();
    this._updateDrawerPickerHeader();
    this._syncDrawerFooter();
  }

  _removeLastInstanceOfColor(colorName) {
    const normalized = this.cleanPriceText(colorName || "");
    if (!normalized) return;

    const slots = this._getAllSelectableSlots();
    for (let i = slots.length - 1; i >= 0; i--) {
      const name = this._getOptionColorName(slots[i]);
      if (name === normalized) {
        this._removeSlotSelection(slots[i]);
        return;
      }
    }
  }

  _createDrawerMiniDeleteButton() {
    const closeBtn = document.createElement("div");
    closeBtn.className = "deleteOption set-drawer__mini-delete";
    closeBtn.setAttribute("role", "button");
    closeBtn.setAttribute("aria-label", "Farbe entfernen");
    closeBtn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `;
    return closeBtn;
  }

  _syncDrawerChooser() {
    const chooser = this.querySelector("#set-drawer-chooser");
    if (!chooser) return;

    const required = this._getRequiredSlotCount();
    const slots = this._getServerSlots();

    // Pflicht-Slots (leere als Platzhalter) plus die Bonus-Slots, damit
    // gewählte Zusatzfarben im Drawer sichtbar und entfernbar sind.
    const entries = [];
    for (let i = 0; i < required; i++) entries.push(slots[i]);
    this._getOptionalSlots().forEach((slot) => entries.push(slot));

    const nextSlot = this._getFirstEmptyTargetSlot();

    chooser.innerHTML = "";

    for (let i = 0; i < entries.length; i++) {
      const outerSlot = entries[i];
      const item = document.createElement("div");
      item.className = "set-drawer__chooser-item";
      item.setAttribute("role", "listitem");
      item.dataset.slotIndex = String(i);

      const design = document.createElement("div");
      design.className = "design set-drawer__mini-design";

      if (this._isOptionalSlot(outerSlot)) {
        item.classList.add("is-optional");
      }

      const img = outerSlot?.querySelector(".design img");
      if (this._isSlotFilled(outerSlot)) {
        item.classList.add("is-filled");
        if (img?.src) {
          const thumb = document.createElement("img");
          thumb.src = img.src;
          thumb.alt = this._getOptionColorName(outerSlot) || "";
          design.appendChild(thumb);
        }
        design.classList.add("hide-after");
        design.style.backgroundColor = "white";
        item.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this._removeSlotSelection(outerSlot);
        });
        item.appendChild(design);
        item.appendChild(this._createDrawerMiniDeleteButton());
      } else {
        item.classList.add("is-empty");
        if (outerSlot && outerSlot === nextSlot) {
          item.classList.add("is-next");
          if (item.classList.contains("is-optional")) {
            const percent =
              this._getNextColorDiscountPercent() ||
              this._getOptionalPricesForSlot(outerSlot)?.percent ||
              Math.round(GDN_OPTIONAL_COLOR_DISCOUNT * 100);
            if (percent) {
              const label = document.createElement("span");
              label.className = "set-drawer__discount-label";
              label.textContent = `−${percent}%`;
              design.appendChild(label);
            }
          }
        }
        item.appendChild(design);
      }

      chooser.appendChild(item);
    }
  }

  _syncSwatchQtyOverlays() {
    const sideDrawer = this.querySelector("#side-drawer");
    if (!sideDrawer?.classList.contains("open")) return;

    sideDrawer.querySelectorAll(".hulkapps_swatch_option").forEach((label) => {
      const colorName = this.cleanPriceText(
        label.dataset.swatchLabel || label.getAttribute("aria-label") || ""
      );
      const qty = colorName ? this._getColorQuantity(colorName) : 0;
      let overlay = label.querySelector(".gdn-swatch-qty-overlay");

      if (qty <= 0) {
        overlay?.remove();
        return;
      }

      const previewHost =
        label.querySelector(".swatch-preview") ||
        label.querySelector(".main-carousel");

      if (!overlay) {
        overlay = document.createElement("div");
        overlay.className = "gdn-swatch-qty-overlay";
        // Bewusst <span> statt <button>: die Overlays sitzen in einem <label>.
        // Ein labelbares Element (button/input) würde beim Label-Klick zur
        // "label activation" des Browsers und damit zu einem zweiten,
        // synthetischen Klick auf den Minus-Button führen - die gerade
        // gewählte Farbe würde sofort wieder entfernt.
        overlay.innerHTML = `
          <span role="button" class="gdn-swatch-qty-btn gdn-swatch-qty-btn--minus" aria-label="Menge verringern">−</span>
          <span class="gdn-swatch-qty-count"></span>
          <span role="button" class="gdn-swatch-qty-btn gdn-swatch-qty-btn--plus" aria-label="Menge erhöhen">+</span>
        `;

        overlay
          .querySelector(".gdn-swatch-qty-btn--minus")
          ?.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            this._removeLastInstanceOfColor(colorName);
          });

        overlay
          .querySelector(".gdn-swatch-qty-btn--plus")
          ?.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (event.currentTarget.classList.contains("is-disabled")) return;
            const emptySlot = this._getFirstEmptyTargetSlot();
            if (emptySlot) {
              this._addSwatchToSlot(emptySlot, label);
            }
          });

        if (previewHost) {
          previewHost.appendChild(overlay);
        } else {
          label.appendChild(overlay);
        }
      }

      overlay.querySelector(".gdn-swatch-qty-count").textContent =
        String(qty);

      const plusBtn = overlay.querySelector(".gdn-swatch-qty-btn--plus");
      if (plusBtn) {
        const disabled = !this._canAddMoreSelections();
        plusBtn.setAttribute("aria-disabled", String(disabled));
        plusBtn.classList.toggle("is-disabled", disabled);
      }
    });

    this._syncSwatchDiscountBadges();
  }

  /**
   * Rabatt, den die NÄCHSTE Farbe bekommt. Später kommt der Wert aus der
   * Preisstaffel des Produkts, weil er je Set und je Stufe unterschiedlich ist
   * (Starter Set und Mix & Match haben eigene Staffeln). Bis dahin die eine
   * Stufe, die das Essentials Set kennt.
   */
  _getNextColorDiscountPercent() {
    if (!this._areAllRequiredSlotsFilled()) return 0;
    if (!this._canAddMoreSelections()) return 0;

    const nextCount =
      this._getRequiredSlotCount() + this._getFilledOptionalSlots().length + 1;
    const unit = this._getOptionalUnitPrices(nextCount);
    if (unit?.percent) return unit.percent;

    return Math.round(GDN_OPTIONAL_COLOR_DISCOUNT * 100);
  }

  /**
   * Dezentes Rabatt-Badge auf den Drawer-Farben, solange die nächste Farbe eine
   * Zusatzfarbe wäre. Nicht auf bereits gewählten Farben - dort sitzt das
   * Mengen-Overlay an derselben Stelle.
   */
  _syncSwatchDiscountBadges() {
    const sideDrawer = this.querySelector("#side-drawer");
    if (!sideDrawer?.classList.contains("open")) return;

    const percent = this._getNextColorDiscountPercent();

    sideDrawer.querySelectorAll(".hulkapps_swatch_option").forEach((label) => {
      const colorName = this.cleanPriceText(
        label.dataset.swatchLabel || label.getAttribute("aria-label") || ""
      );
      const isSelected = colorName
        ? this._getColorQuantity(colorName) > 0
        : false;
      let badge = label.querySelector(".gdn-swatch-discount-badge");

      if (!percent || isSelected) {
        badge?.remove();
        return;
      }

      // Nur in die Bildvorschau hängen, nie ins Label: die Vorschau wird lazy
      // aufgebaut, und ohne sie hätte das Badge keinen positionierten Anker und
      // würde als Balken quer über den Drawer laufen. Nach dem Aufbau des
      // Karussells läuft dieser Sync erneut.
      const previewHost =
        label.querySelector(".swatch-preview") ||
        label.querySelector(".main-carousel");

      if (!previewHost) {
        badge?.remove();
        return;
      }

      if (!badge) {
        badge = document.createElement("div");
        badge.className = "gdn-swatch-discount-badge";
        badge.setAttribute("aria-hidden", "true");
        previewHost.appendChild(badge);
      }

      badge.textContent = `−${percent}%`;
    });
  }

  _updateDrawerConfirmButton() {
    const btn = this.querySelector("#set-drawer-confirm");
    if (!btn) return;

    const filled = this._getFilledServerSlotCount();
    const required = this._getRequiredSlotCount();
    const isComplete = required > 0 && filled >= required;

    btn.hidden = !isComplete;
    btn.disabled = !isComplete;
  }

  _onDrawerOpenedForPicker() {
    this._ensurePickerTargetSlot();
    this._showDrawerSwatchGrid();
    this._updateDrawerPickerHeader();
    this.setupSwatchListeners();
    this._syncDrawerFooter();
    this._syncSwatchQtyOverlays();
    this._refreshDrawerSwatchObserver();
    this._scheduleDrawerFooterSync();
  }

  /**
   * Stellt sicher, dass beim Farbwählen im Drawer immer ein gültiger
   * Ziel-Slot gesetzt ist — sonst zeigt Hulk nur den grünen Rahmen,
   * handleSwatchClick bricht aber bei !targetSlot ab.
   */
  _ensurePickerTargetSlot() {
    if (this._pendingSlot && this.contains(this._pendingSlot)) return;

    const nextEmpty = this._getFirstEmptyTargetSlot();
    if (nextEmpty) {
      this.setActiveSlot(nextEmpty);
      return;
    }

    const active = this.querySelector(
      ".custom-options-enterprise .option.active"
    );
    if (active && this.contains(active)) {
      this._pendingSlot = active;
    }
  }

  _resolvePickerTargetSlot() {
    if (this._pendingSlot && this.contains(this._pendingSlot)) {
      return this._pendingSlot;
    }

    const nextEmpty = this._getFirstEmptyTargetSlot();
    if (nextEmpty) return nextEmpty;

    return (
      this.querySelector(".custom-options-enterprise .option.active") || null
    );
  }

  /**
   * Swatch-Vorschauen lazy laden (IntersectionObserver + sichtbare Erste).
   * Nicht alle Swatches auf einmal bauen — bei Shellac sind das 100+ Labels.
   */
  _refreshDrawerSwatchObserver() {
    const sideDrawer = this.querySelector("#side-drawer");
    if (!sideDrawer) return;

    if (!this._carouselObserver) {
      this._setupCarouselObserver();
    }

    sideDrawer.querySelectorAll(".hulkapps_swatch_option").forEach((label) => {
      if (label.dataset.carouselObserved !== "true") {
        label.dataset.carouselObserved = "true";
        this._carouselObserver?.observe(label);
        return;
      }
      // Drawer war off-screen → IO neu anstoßen
      this._carouselObserver?.unobserve(label);
      this._carouselObserver?.observe(label);
    });

    // Erste sichtbare Reihe sofort (max. 12), Rest per Scroll/Hover
    requestAnimationFrame(() => {
      if (!sideDrawer.classList.contains("open")) return;

      const visibleGroup = sideDrawer.querySelector(
        '.hulkapps_option.swatch_render:not([style*="none"])'
      );
      if (!visibleGroup) return;

      const labels = visibleGroup.querySelectorAll(".hulkapps_swatch_option");
      labels.forEach((label) => this._normalizeDrawerSwatchLabelText(label));
      const limit = Math.min(labels.length, 12);
      for (let i = 0; i < limit; i++) {
        this._normalizeDrawerSwatchLabelText(labels[i]);
        this._buildCarouselForLabel(labels[i]);
      }
    });
  }

  setupDrawerFooter() {
    const confirmBtn = this.querySelector("#set-drawer-confirm");
    if (!confirmBtn || confirmBtn.dataset.listenerAttached) return;

    confirmBtn.dataset.listenerAttached = "true";
    confirmBtn.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.closeDrawer();
    });
  }

  cleanup() {
    if (this._blurOverlayHideTimer) {
      clearTimeout(this._blurOverlayHideTimer);
      this._blurOverlayHideTimer = null;
    }
    this._syncBlurOverlay(false);
    this._hideLoader();
    this._unlockPageScroll();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.priceObserverInterval) {
      clearInterval(this.priceObserverInterval);
      this.priceObserverInterval = null;
    }
    if (this._onVariantChange) {
      document.removeEventListener("variant:change", this._onVariantChange);
      this._onVariantChange = null;
    }
    if (this._onProductRerender) {
      document.removeEventListener("product:rerender", this._onProductRerender);
      this._onProductRerender = null;
    }
    if (this._onNextButtonClick) {
      document.removeEventListener("click", this._onNextButtonClick, true);
      this._onNextButtonClick = null;
    }
    if (this._onCarouselPointerOver) {
      document.removeEventListener(
        "pointerover",
        this._onCarouselPointerOver
      );
      this._onCarouselPointerOver = null;
    }
    if (this._carouselObserver) {
      this._carouselObserver.disconnect();
      this._carouselObserver = null;
    }
    if (this._carouselMutationObserver) {
      this._carouselMutationObserver.disconnect();
      this._carouselMutationObserver = null;
    }
    if (this._onPreventTouchMove) {
      document.removeEventListener("touchmove", this._onPreventTouchMove, {
        passive: false,
      });
    }
  }

  _preventTouchMoveWhileDrawerOpen(event) {
    const sideDrawer = this.querySelector("#side-drawer");
    if (!this._pageScrollLocked || !sideDrawer?.classList.contains("open")) {
      return;
    }

    if (
      event.target.closest(
        "#side-drawer .set-drawer__body, #side-drawer .set-drawer__chooser"
      )
    ) {
      return;
    }

    event.preventDefault();
  }

  _lockPageScroll() {
    if (this._pageScrollLocked) return;

    this._pageScrollLocked = true;
    this._lockedScrollY =
      window.scrollY || document.documentElement.scrollTop || 0;

    document.documentElement.classList.add("set-configurator-scroll-lock");
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.setProperty("position", "fixed");
    document.body.style.setProperty("top", `-${this._lockedScrollY}px`);
    document.body.style.setProperty("left", "0");
    document.body.style.setProperty("right", "0");
    document.body.style.setProperty("width", "100%");

    this._onPreventTouchMove =
      this._onPreventTouchMove ||
      this._preventTouchMoveWhileDrawerOpen.bind(this);
    document.addEventListener("touchmove", this._onPreventTouchMove, {
      passive: false,
    });
  }

  _unlockPageScroll() {
    if (!this._pageScrollLocked) return;

    this._pageScrollLocked = false;

    if (this._onPreventTouchMove) {
      document.removeEventListener("touchmove", this._onPreventTouchMove, {
        passive: false,
      });
    }

    document.documentElement.classList.remove("set-configurator-scroll-lock");
    document.documentElement.style.overflow = "";
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("position");
    document.body.style.removeProperty("top");
    document.body.style.removeProperty("left");
    document.body.style.removeProperty("right");
    document.body.style.removeProperty("width");
    window.scrollTo(0, this._lockedScrollY || 0);
  }

  _syncDrawerScrollLock(isOpen) {
    if (isOpen) {
      this._lockPageScroll();
    } else {
      this._unlockPageScroll();
    }
  }

  _syncBlurOverlay(isOpen) {
    const blurOverlay =
      this.querySelector("#blur-overlay") || document.getElementById("blur-overlay");
    if (!blurOverlay) return;

    if (this._blurOverlayHideTimer) {
      clearTimeout(this._blurOverlayHideTimer);
      this._blurOverlayHideTimer = null;
    }

    if (isOpen) {
      if (blurOverlay.parentElement !== document.body) {
        document.body.appendChild(blurOverlay);
      }
      blurOverlay.style.display = "block";
      blurOverlay.style.zIndex = "1000";
      requestAnimationFrame(() => {
        blurOverlay.classList.add("is-active");
      });
      return;
    }

    blurOverlay.classList.remove("is-active");
    this._blurOverlayHideTimer = window.setTimeout(() => {
      if (!blurOverlay.classList.contains("is-active")) {
        blurOverlay.style.display = "none";
        blurOverlay.style.zIndex = "";
      }
      this._blurOverlayHideTimer = null;
    }, 240);
  }

  _hideLoader() {
    const loader = this.querySelector(".loader");
    if (loader) loader.style.display = "none";
  }

  /**
   *
   * Reihenfolge:
   * 1. Pattern `(\d+)\s*Farbe` in option1/2/3 des Variant-Objekts.
   * 2. Fallback: angereicherter Datensatz aus `script[data-variant-detail-data]`
   *    (siehe variant-detail-tile.liquid - dort wird option1 mit dem
   *    count_label angereichert, wenn die rohe option1 keine Farb-Anzahl
   *    enthält). Dadurch funktioniert das auch für Varianten, die
   *    "Basic"/"Pro"/"Plus" heißen ohne "X Farbe" im Namen.
   * 3. Letzter Versuch: `(\d+)\s*Farbe`-Pattern direkt im count_label des
   *    angereicherten Datensatzes.
   *
   * Returns null, wenn keine der Quellen die Anzahl liefert.
   */
  getSlotConfig() {
    const opts = this.parsedColorOptions || {};
    let slotSingular = opts.slotSingular || opts.slotLabel || "";

    if (!slotSingular) {
      const firstName = opts.options?.[0]?.name || "";
      slotSingular = /top\s*coat/i.test(firstName) ? "Top Coat" : "Farbe";
    }

    const isTopCoat = /top\s*coat/i.test(slotSingular);

    const defaultNextPickBanner = isTopCoat
      ? "Bitte wähle deinen nächsten Top Coat"
      : "Bitte wähle deine nächste Farbe";
    let nextPickBanner = opts.nextPickBanner || defaultNextPickBanner;
    if (/wählen\s+sie\s+ihre\s+nächste\s+farbe/i.test(nextPickBanner)) {
      nextPickBanner = "Bitte wähle deine nächste Farbe";
    }

    let pickerTitle =
      opts.pickerTitle ||
      (isTopCoat ? "Wähle deinen Top Coat" : "Wähle deine Farben");
    if (/teil\s+deines/i.test(pickerTitle)) {
      pickerTitle = isTopCoat ? "Wähle deinen Top Coat" : "Wähle deine Farben";
    }
    if (/^wähle deine farbe:?$/i.test(pickerTitle.trim())) {
      pickerTitle = "Wähle deine Farben";
    }

    return {
      slotSingular,
      pickerTitle,
      nextPickBanner,
      chooseButtonPrefix:
        opts.chooseButtonPrefix ||
        (isTopCoat ? "WÄHLE DEINEN TOP COAT" : "WÄHLE DEINE FARBE"),
    };
  }

  normalizeSlotDisplayName(name) {
    if (!name) return name;
    const text = String(name).trim();
    const farbeMatch = text.match(/^Farbe\s+0*(\d+)$/i);
    if (farbeMatch) {
      return `Farbe ${parseInt(farbeMatch[1], 10)}`;
    }
    return text;
  }

  normalizeExistingSlotLabels() {
    this.querySelectorAll(".option p").forEach((paragraph) => {
      const original =
        paragraph.getAttribute("data-original-text") || paragraph.textContent;
      const normalized = this.normalizeSlotDisplayName(original);
      paragraph.setAttribute("data-original-text", normalized);
      if (paragraph.textContent.trim() === String(original).trim()) {
        paragraph.textContent = normalized;
      }
    });
  }

  getDefaultSlotName(index) {
    const { slotSingular } = this.getSlotConfig();
    if (/top\s*coat/i.test(slotSingular)) {
      return `Top Coat ${String(index).padStart(2, "0")}`;
    }
    return `${slotSingular} ${index}`;
  }

  /**
   * Liefert den Anzeigenamen einer gewählten Farbe (nicht den Slot-Platzhalter
   * wie „Farbe 1“ aus data-original-text).
   */
  _getOptionColorName(optionEl) {
    if (!optionEl) return null;

    const p = optionEl.querySelector(":scope > p");
    if (!p?.textContent) return null;

    const text = this.cleanPriceText(p.textContent);
    const original = (p.getAttribute("data-original-text") || "").trim();
    if (original && text === original) return null;

    return text || null;
  }

  _getBannerSuccessColorName() {
    if (this._lastSelectedColorName) {
      return this.cleanPriceText(this._lastSelectedColorName);
    }

    const activeOption = this.querySelector(
      ".custom-options-enterprise .option.active"
    );
    const fromActive = this._getOptionColorName(activeOption);
    if (fromActive) return fromActive;

    const filledOptions = Array.from(
      this.querySelectorAll(
        ".custom-options-enterprise .option:not([data-new-container])"
      )
    );

    for (let i = filledOptions.length - 1; i >= 0; i--) {
      const name = this._getOptionColorName(filledOptions[i]);
      if (name) return name;
    }

    return null;
  }

  applyPickerCopy() {
    const { pickerTitle, nextPickBanner } = this.getSlotConfig();
    const titleEl = this.querySelector(".custom-options-title");
    const bannerVarText = this.querySelector("#bannerVariantText");

    if (titleEl) {
      titleEl.textContent = pickerTitle;
    }

    if (bannerVarText) {
      bannerVarText.textContent = nextPickBanner;
    }

    this.getDataCount();
  }

  getColorCountFromVariant(variant) {
    if (!variant) return null;

    const options = [variant.option1, variant.option2, variant.option3];
    for (const option of options) {
      const count = gdnParseSlotCountFromText(option);
      if (count !== null) {
        return count;
      }
    }

    return this._lookupCountFromVariantDetail(variant.id);
  }

  /**
   * Sucht in allen `script[data-variant-detail-data]`-Tags nach dem Variant
   * mit der gegebenen ID und versucht, daraus die Farb-Anzahl zu extrahieren.
   * Diese Tags werden in variant-detail-tile.liquid pro product_form_id
   * gerendert und enthalten ein angereichertes option1 + count_label.
   */
  _lookupCountFromVariantDetail(variantId) {
    if (!variantId) return null;

    const scripts = document.querySelectorAll(
      "script[data-variant-detail-data]"
    );
    for (const script of scripts) {
      let parsed;
      try {
        parsed = JSON.parse(script.textContent || "[]");
      } catch (e) {
        continue;
      }
      if (!Array.isArray(parsed)) continue;

      const entry = parsed.find((v) => v && v.id === variantId);
      if (!entry) continue;

      const candidates = [
        entry.option1,
        entry.option2,
        entry.option3,
        entry.count_label,
      ];
      for (const candidate of candidates) {
        const count = gdnParseSlotCountFromText(candidate);
        if (count !== null) {
          return count;
        }
      }
    }
    return null;
  }

  /**
   * Liest die Farbanzahl aus der initial gewählten Variante beim Seitenaufruf.
   *
   * Reihenfolge:
   * 1. Variant-Pattern: `(\d+)\s*Farbe` in option1/2/3 (z.B. "3 Farben Set").
   * 2. Fallback: Anzahl der serverseitig gerenderten Slots im DOM
   *    (.custom-options-enterprise .option:not([data-new-container])).
   *    Das Liquid in custom-hulk-drawer.liquid hat einen eigenen Fallback auf
   *    color_data.options.size, falls der Variant-Name nichts hergibt - das
   *    Server-Rendering ist also die zuverlässige Source of Truth, wenn der
   *    Variant-Name keine Farb-Anzahl enthält (z.B. "Basic"/"Pro"/"Plus").
   *
   * Ohne Fix B greift der Schutz in checkAndAppendContainers nicht, sobald
   * activeColorCount null ist - dann erscheint nach dem Befüllen aller Slots
   * fälschlich ein zusätzlicher "Optionale Farbe"-Slot.
   */
  getInitialColorCount() {
    const variantScript = document.querySelector("script[data-variant]");
    if (variantScript) {
      try {
        const variant = JSON.parse(variantScript.textContent);
        const fromVariant = this.getColorCountFromVariant(variant);
        if (fromVariant !== null) return fromVariant;
      } catch (e) {
        // fall through to DOM-Fallback
      }
    }

    const serverSlots = this.querySelectorAll(
      ".custom-options-enterprise .option:not([data-new-container])"
    ).length;
    return serverSlots > 0 ? serverSlots : null;
  }

  /**
   * Entfernt überschüssige Container beim initialen Laden (bevor Setup-Methoden laufen).
   */
  trimContainersToCount(count) {
    const enterprise = this.querySelector(".custom-options-enterprise");
    if (!enterprise) return;

    const containers = enterprise.querySelectorAll(".option");
    for (let i = containers.length - 1; i >= count; i--) {
      containers[i].remove();
    }
  }

  /**
   * Setzt die Farbauswahl EINES Containers zurück: entfernt das Bild, setzt den
   * Text auf den Original-Wert, blendet den Close-Button aus und resettet die
   * gekoppelte Hulk-Swatch-Property (damit nichts ungewollt im Cart landet).
   */
  _clearSelectionForContainer(container) {
    if (!container) return;

    const img = container.querySelector(".design img");
    const design = container.querySelector(".design");
    const paragraph = container.querySelector(":scope > p");
    const closeBtn = container.querySelector(".deleteOption");

    if (img) {
      img.remove();
      if (design) {
        design.classList.remove("hide-after");
        design.style.backgroundColor = "#f1edea";
      }
    }

    if (paragraph) {
      const original =
        paragraph.getAttribute("data-original-text") || paragraph.textContent;
      paragraph.textContent = original;
    }

    if (closeBtn) {
      closeBtn.style.display = "none";
    }

    this.updateSwatchPriceOnClose(container);
  }

  /**
   * Setzt alle Farbauswahlen in allen Containern zurück (Bilder, Texte, Hulk-Swatches).
   */
  clearAllSelections() {
    this.querySelectorAll(".custom-options-enterprise .option").forEach(
      (container) => this._clearSelectionForContainer(container)
    );
  }

  /**
   * Lauscht auf Varianten-Wechsel und passt die Farbcontainer-Anzahl an.
   *
   * Zwei zusätzliche Sicherheitsnetze für den "Wähle deine Farbe"-Button:
   * 1. Auf product:rerender wird createNextContainerButton() neu aufgerufen,
   *    weil <product-rerender> die <product-payment-container> samt #nextContainerButton
   *    austauscht (theme.js Zeile 5390-5391) und damit den ursprünglichen Click-Listener killt.
   * 2. Click-Delegation auf document in Capture-Phase: fängt Klicks auf
   *    #nextContainerButton / #sticky_nextContainerButton ab, BEVOR der Form-Submit
   *    feuern könnte, und ruft openNextContainerWithoutImage() direkt auf.
   *    Damit ist der Button auch bei Race-Conditions sicher klickbar.
   */
  setupVariantListener() {
    /**
     * Re-sync Button-Sichtbarkeit + -Text NACHDEM das Theme die Buttons neu
     * gerendert hat. Nötig, weil <product-rerender> beim Variant-Wechsel
     * <product-payment-container> komplett austauscht (theme.js ~5390) und
     * der neue #nextContainerButton mit dem Liquid-Default kommt:
     * "display: block" + Text "Wähle deine Farbe 01". Ohne diesen Re-Sync
     * sieht der User beim Wechsel von Pro (alle 6 Slots gefüllt) auf Basic
     * (3 Slots, weiter gefüllt) den Wähle-Farbe-Button statt Add-to-Cart.
     */
    this._resyncAfterRerender = () => {
      this.createNextContainerButton();
      this.updateBannerMessage();
      this.getDataCount();
      this._syncAddToCartPrice();
    };

    this._onVariantChange = (event) => {
      const variant = event.detail?.variant;
      if (!variant) return;

      if (variant.option1) {
        this._selectedSetType = variant.option1;
      } else if (Array.isArray(variant.options) && variant.options[0]) {
        this._selectedSetType = variant.options[0];
      }

      const colorCount = this.getColorCountFromVariant(variant);
      if (colorCount !== null) {
        this.updateContainerCount(colorCount);
      }

      // Fallback-Re-Sync: das Theme dispatcht `product:rerender` direkt nach
      // `variant:change` auf dem <form>-Element (OHNE bubbles, daher nicht
      // auf document erreichbar). Wir warten kurz und re-syncen die Button-
      // Sichtbarkeit, falls der direkte Form-Listener (siehe unten) noch
      // nicht gebunden war oder der rerender länger dauert.
      setTimeout(() => this._resyncAfterRerender(), 250);
    };

    /**
     * Direktes Lauschen auf <form>-Ebene. `product:rerender` bubblet NICHT,
     * deshalb müssen wir den Listener direkt am Form anhängen statt am
     * document. Falls das Form (noch) nicht im DOM ist, polled wir kurz mit
     * MutationObserver und binden, sobald es da ist.
     */
    this._onProductRerender = () => this._resyncAfterRerender();

    const bindFormRerender = (form) => {
      if (!form || form.dataset.setConfiguratorRerenderBound === "1") return;
      form.dataset.setConfiguratorRerenderBound = "1";
      form.addEventListener("product:rerender", this._onProductRerender);
    };

    const bindFormSubmit = (form) => {
      if (!form || form.dataset.setConfiguratorSubmitBound === "1") return;
      form.dataset.setConfiguratorSubmitBound = "1";
      form.addEventListener(
        "submit",
        () => {
          this._syncAllHulkHiddenPropertiesFromChooser();
        },
        true
      );
    };

    const findAndBindForm = () => {
      const form = document.querySelector(
        "form[is='product-form'], form.shopify-product-form"
      );
      if (form) {
        bindFormRerender(form);
        bindFormSubmit(form);
        return true;
      }
      return false;
    };

    if (!findAndBindForm()) {
      // Form noch nicht im DOM (z.B. async Theme-Init) → einmalig Observer.
      const observer = new MutationObserver(() => {
        if (findAndBindForm()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      // Sicherheits-Cap: nach 5 s aufgeben, damit kein Dauer-Observer läuft.
      setTimeout(() => observer.disconnect(), 5000);
    }

    this._onNextButtonClick = (event) => {
      const btn = event.target.closest(
        "#nextContainerButton, #sticky_nextContainerButton"
      );
      if (!btn) return;

      event.preventDefault();
      event.stopPropagation();

      if (typeof this.openNextContainerWithoutImage === "function") {
        this.openNextContainerWithoutImage();
      }
    };

    document.addEventListener("variant:change", this._onVariantChange);
    document.addEventListener("click", this._onNextButtonClick, true);
  }

  /**
   * Passt die Anzahl der Farbcontainer dynamisch an (z.B. bei Variant-Wechsel).
   *
   * Verhalten:
   * - Slots 0..count-1 behalten ihre aktuelle Auswahl. Damit bleibt die
   *   Auswahl beim Wechsel von z.B. Pro (5 Farben) zu Plus (3 Farben)
   *   erhalten - nur die überschüssigen Slots 4+5 werden entfernt.
   * - Beim Verkleinern: die zu entfernenden Slots werden VOR dem Entfernen
   *   geleert (inkl. Hulk-Property-Reset über _clearSelectionForContainer),
   *   damit ihre versteckten Hulk-Selektionen nicht im Cart landen.
   * - Beim Vergrößern: neue leere Slots werden hinten angehängt.
   *
   * Bonus-Slots ("Optionale Farbe") werden zurückgesetzt: die Set-Größe und
   * damit der Basispreis ändern sich, außerdem müssen neue Pflicht-Slots vor
   * den Bonus-Slots einsortiert bleiben.
   */
  updateContainerCount(count) {
    this.activeColorCount = count;
    this._resetOptionalSlots();

    const enterprise = this.querySelector(".custom-options-enterprise");
    if (!enterprise) return;

    const existingContainers = enterprise.querySelectorAll(
      ".option:not([data-new-container])"
    );
    const currentCount = existingContainers.length;

    if (currentCount > count) {
      for (let i = currentCount - 1; i >= count; i--) {
        const slot = existingContainers[i];
        // _pendingSlot könnte auf den zu entfernenden Slot zeigen - dann
        // zurücksetzen, sonst zielt handleSwatchClick auf einen detached node.
        if (this._pendingSlot === slot) {
          this._pendingSlot = null;
        }
        this._clearSelectionForContainer(slot);
        slot.remove();
      }
    }

    if (currentCount < count) {
      const options = this.parsedColorOptions?.options || [];
      for (let i = currentCount + 1; i <= count; i++) {
        const optionData = options[i - 1];
        const name = this.normalizeSlotDisplayName(
          optionData?.name || this.getDefaultSlotName(i)
        );

        const optionHtml = `
          <div class="option option-${i}" data-count="${i}">
            <div class="design design-${i}"></div>
            <p data-original-text="${name}">${name}</p>
            <div class="deleteOption design-${i}" style="display: none;">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </div>
          </div>
        `;
        enterprise.insertAdjacentHTML("beforeend", optionHtml);

        const newContainer = enterprise.querySelector(`.option-${i}`);
        const closeBtn = newContainer?.querySelector(".deleteOption");
        if (closeBtn) {
          closeBtn.addEventListener("click", (e) =>
            this.handleCloseClick(e, closeBtn)
          );
        }

        setTimeout(() => {
          newContainer?.classList.add("animate");
        }, (i - currentCount) * 100);
      }
    }

    this.syncHulkOptions(count);
    this.setupEventListeners();
    this.updateSelectionStatus();
    this.updateOptionIndexes();
    this.updateButtonVisibility();
    this.checkMatchingOpt();
    this.setupSwatchListeners();
    this.setupDrawer();
    this.setupDrawerFooter();
    this.createCustomContainerLabels();
    this.getCurrentActiveCont();
    this.getDataCount();
    this.updateBannerMessage();
    this._syncOptionalSlots();

    const sideDrawer = this.querySelector("#side-drawer");
    if (sideDrawer?.classList.contains("open")) {
      this._syncDrawerFooter();
      this._syncSwatchQtyOverlays();
      this._updateDrawerPickerHeader();
    }
  }

  /**
   * Blendet Hulk-Swatch-Optionsets ein/aus, passend zur gewählten Farbanzahl.
   *
   * WICHTIG: zusätzlich zum CSS-Verstecken werden die `<input name="...">`-
   * Felder in den versteckten Sets `disabled` gesetzt. Browser submitten
   * `display: none`-Inputs ganz normal mit dem Form - ohne `disabled` würden
   * also beim Wechsel von z.B. Pro (3 gewählte Farben) auf Basic (1 Farbe)
   * trotzdem alle 3 Farben als Line-Item-Properties im Cart landen, obwohl
   * der Add-to-Cart den günstigeren Basic-Preis bucht. Mit `disabled` werden
   * nur die N tatsächlich aktiven Farben mitgeschickt. Beim Wechsel auf eine
   * größere Variante (Basic→Pro) werden die Inputs wieder enabled, sodass
   * spätere Auswahlen wieder funktionieren.
   */
  syncHulkOptions(count) {
    this._markOptionalHulkGroups();

    this._getAllHulkGroups().forEach((opt, index) => {
      // Optionale Gruppen hängen nicht an der Variant-Farbanzahl, sondern am
      // Füllstand ihres Bonus-Slots - siehe _syncOptionalHulkGroupState.
      if (opt.dataset.gdnOptional === "true") return;

      const isActive = index < count;
      opt.style.display = isActive ? "" : "none";

      const inputs = opt.querySelectorAll(
        "input[name], select[name], textarea[name]"
      );
      inputs.forEach((input) => {
        input.disabled = !isActive;
      });
    });

    this._syncOptionalHulkGroupState();
  }

  /**
   * Optionale Hulk-Gruppen bleiben immer im DOM, dürfen aber nur dann
   * Line-Item-Properties submitten, wenn ihr Bonus-Slot wirklich eine Farbe
   * enthält. Sonst landen leere "Optionale Farbe"-Zeilen im Warenkorb.
   */
  _syncOptionalHulkGroupState() {
    const usedClasses = new Set(
      this._getFilledOptionalSlots()
        .map((slot) => this._getSlotClassFromElement(slot))
        .filter(Boolean)
    );

    this._getOptionalHulkGroups().forEach((group) => {
      const groupClass = this._getSlotClassFromElement(group);
      const isUsed = !!groupClass && usedClasses.has(groupClass);

      // Nie direkt im Drawer sichtbar - die Auswahl läuft immer über die
      // erste Gruppe und wird per Slot-Klasse in die Zielgruppe gespiegelt.
      group.style.display = "none";

      group
        .querySelectorAll("input[name], select[name], textarea[name]")
        .forEach((input) => {
          input.disabled = !isUsed;
        });

      if (!isUsed) {
        const hiddenProp = group.querySelector(".hulk_swatch_hidden_prop");
        if (hiddenProp) hiddenProp.value = "";
      }
    });
  }

  handleNoColorOptions() {
    const farbeParent = this.querySelector("#farbeParent");
    const loadAssets = this.querySelector(".load-assets");
    const addToCart = document.querySelector(
      ".product-form__payment-container #AddToCart"
    );
    const quantitySelector = document.querySelector(".product-form__quantity");

    if (farbeParent) farbeParent.remove();
    if (loadAssets) loadAssets.style.display = "none";
    if (addToCart) addToCart.style.display = "block";
    if (quantitySelector) quantitySelector.style.display = "block";
  }

  /**
   * Wartet auf HulkApps Custom Options - Event-basiert statt Polling
   */
  waitForHulkApps() {
    return new Promise((resolve) => {
      const checkHulkApps = () => {
        const hulkContainer = document.querySelector(".hulkapps_option_set");
        if (hulkContainer) {
          resolve();
          return true;
        }
        return false;
      };

      // Sofort prüfen
      if (checkHulkApps()) return;

      // MutationObserver für HulkApps - viel effizienter als setInterval
      const targetNode = document.body;
      const config = { childList: true, subtree: true };

      const observerCallback = (mutationsList, obs) => {
        if (checkHulkApps()) {
          obs.disconnect();
        }
      };

      const observer = new MutationObserver(observerCallback);
      observer.observe(targetNode, config);

      // Timeout nach 10 Sekunden
      setTimeout(() => {
        observer.disconnect();
        resolve(); // Trotzdem initialisieren
      }, 10000);
    });
  }

  async init() {
    const customOptionsEnterprise = this.querySelector(
      ".custom-options-enterprise"
    );
    const farbeParent = this.querySelector("#farbeParent");

    if (!customOptionsEnterprise) {
      console.warn("SetConfigurator: .custom-options-enterprise not found");
      return;
    }

    // Zeige Container
    if (farbeParent) farbeParent.style.display = "block";

    // Alle Initialisierungen ausführen
    this.appendContainersFromMetafields();
    this.normalizeExistingSlotLabels();

    // Container-Anzahl an initial gewählte Variante anpassen
    const initialCount = this.getInitialColorCount();
    if (initialCount !== null) {
      this.activeColorCount = initialCount;
      this.trimContainersToCount(initialCount);
    }

    this._markOptionalHulkGroups();
    this._resetOptionalSlots();

    this.setupEventListeners();
    this.updateSelectionStatus();
    this.updateOptionIndexes();
    this.updateButtonVisibility();
    this.checkMatchingOpt();
    this.setupSwatchListeners();
    this.setupDrawer();
    this.setupDrawerFooter();
    this.createNextContainerButton();
    this.createCustomLabels();
    this.createCustomContainerLabels();
    this.getCurrentActiveCont();
    this.initCustomFields();
    this.calculateDifference();
    this.observePriceChanges();
    this.getDataCount();
    this.setupVariantListener();
    this.applyPickerCopy();
    this._syncOptionalSlots();

    // Bilddaten im Hintergrund prefetchen (non-blocking).
    this.fetchColorProductImages();

    // Karussells nur für sichtbare Swatches bauen (IntersectionObserver).
    // Hover-Listener als Fallback falls der User schneller hovert als der Build.
    this._setupCarouselObserver();
    this._setupCarouselHovers();
    this._setupSwatchPreviewNavigation();

    // Lazy-load Fuse.js für Suche
    await this.initSearch();

    // Loader verstecken
    const loadAssets = this.querySelector(".load-assets");
    if (loadAssets) loadAssets.style.display = "none";

    // Quantity Selector verstecken
    const quantitySelector = document.querySelector(".product-form__quantity");
    if (quantitySelector) quantitySelector.style.display = "none";
  }

  /**
   * Lazy-Load Fuse.js nur wenn benötigt
   */
  async loadFuse() {
    if (this.fuseLoaded || window.Fuse) {
      this.fuseLoaded = true;
      return;
    }

    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/fuse.js@6.6.2/dist/fuse.min.js";
      script.onload = () => {
        this.fuseLoaded = true;
        resolve();
      };
      script.onerror = () => resolve();
      document.head.appendChild(script);
    });
  }

  /**
   * Container aus Metafields erstellen - nur wenn noch nicht serverseitig gerendert
   */
  appendContainersFromMetafields() {
    const customOptionsEnterprise = this.querySelector(
      ".custom-options-enterprise"
    );
    if (!customOptionsEnterprise || !this.parsedColorOptions?.options) return;

    // Prüfen ob Container bereits serverseitig gerendert wurden
    const existingContainers =
      customOptionsEnterprise.querySelectorAll(".option");
    if (existingContainers.length > 0) {
      // Container existieren bereits - nur Animation hinzufügen
      existingContainers.forEach((option, index) => {
        setTimeout(() => {
          option.classList.add("animate");
        }, index * 100);
      });
      return;
    }

    // Fallback: Container dynamisch erstellen wenn nicht serverseitig gerendert
    const options = this.parsedColorOptions.options;

    options.forEach((color, index) => {
      const optionHtml = `
        <div class="option option-${index + 1}" data-count="${index + 1}">
          <div class="design design-${index + 1}"></div>
          <p data-original-text="${this.normalizeSlotDisplayName(color.name)}">${this.normalizeSlotDisplayName(color.name)}</p>
          <div class="deleteOption design-${index + 1}" style="display: none;">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </div>
        </div>
      `;

      customOptionsEnterprise.insertAdjacentHTML("beforeend", optionHtml);

      // Animate
      setTimeout(() => {
        const option = customOptionsEnterprise.querySelector(
          `.option-${index + 1}`
        );
        if (option) option.classList.add("animate");
      }, index * 100);
    });
  }

  /**
   * Event-Listeners für Close-Buttons und Design-Clicks
   */
  setupEventListeners() {
    // Close Button Handler - nur wenn noch nicht attached
    this.querySelectorAll(".deleteOption").forEach((close) => {
      if (close.dataset.listenerAttached) return;
      close.dataset.listenerAttached = "true";
      close.addEventListener("click", (event) =>
        this.handleCloseClick(event, close)
      );
    });

    // Original Text ist jetzt serverseitig via data-original-text gesetzt
    // Nur für dynamisch erstellte Container nachholen
    this.querySelectorAll(".option p").forEach((paragraph) => {
      if (!paragraph.hasAttribute("data-original-text")) {
        paragraph.setAttribute("data-original-text", paragraph.textContent);
      }
    });

    // Hulk-Icon prevent default
    const hulkIcon = this.querySelector(".hulk-icon");
    if (hulkIcon && !hulkIcon.dataset.listenerAttached) {
      hulkIcon.dataset.listenerAttached = "true";
      hulkIcon.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
      });
    }

    // Close drawer icon
    const closeIcon = this.querySelector(
      ".drawer__close-button, .close-drawer-icon, .fa-xmark"
    );
    if (closeIcon && !closeIcon.dataset.listenerAttached) {
      closeIcon.dataset.listenerAttached = "true";
      closeIcon.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeDrawer();
      });
    }
  }

  handleCloseClick(event, close) {
    event.preventDefault();
    event.stopPropagation();

    const loader = this.querySelector(".loader");

    if (loader) loader.style.display = "grid";

    const container = close.parentNode;
    const targetImg = close.closest(".option")?.querySelector("img");
    const siblingEl = close.parentNode.querySelector(".design");
    const paragraph = container.querySelector(":scope > p");

    // Bild entfernen
    if (targetImg) {
      targetImg.remove();
      if (siblingEl) {
        siblingEl.classList.remove("hide-after");
        siblingEl.style.backgroundColor = "#f1edea";
      }
    }

    // Original Text wiederherstellen
    if (paragraph) {
      const original =
        paragraph.getAttribute("data-original-text") || paragraph.textContent;
      paragraph.textContent = original;
    }

    this._lastSelectedColorName = null;

    // Swatch-Preis zurücksetzen (muss vor dem Entfernen laufen, sonst bleibt
    // die Hulk-Property der Gruppe gesetzt und landet im Cart).
    this.updateSwatchPriceOnClose(container);

    // Bonus-Slots sind nie fix: _syncOptionalSlots hängt bei Bedarf einen
    // frischen leeren Slot an.
    if (container?.getAttribute("data-new-container") === "true") {
      if (this._pendingSlot === container) this._pendingSlot = null;
      container.remove();
    }

    this.checkAndAppendContainers();
    this.updateBannerMessage();
    this.getCurrentActiveCont();
    this.updateSelectionStatus();
    this.updateOptionIndexes();
    this.updateButtonVisibility();
    this.getDataCount();

    const sideDrawer = this.querySelector("#side-drawer");
    if (sideDrawer?.classList.contains("open")) {
      this._syncDrawerFooter();
      this._syncSwatchQtyOverlays();
      this._updateDrawerPickerHeader();
    }

    setTimeout(() => {
      this._hideLoader();
    }, 200);
  }

  updateSwatchPriceOnClose(container) {
    if (!container) return;

    const classList = container.classList;
    classList.forEach((cls) => {
      if (cls.startsWith("option-")) {
        document
          .querySelectorAll(".hulkapps_option_set .hulkapps_option")
          .forEach((set) => {
            const hulkToken = Array.from(set.classList).find((c) =>
              c.startsWith("option-")
            );
            if (cls === hulkToken) {
              const swatches = set.querySelectorAll("label [data-radio-class]");
              const swatchVal = set.querySelector(".hulk_swatch_hidden_prop");

              swatches.forEach((selectedSwatch) => {
                if (selectedSwatch.classList.contains("swatch_selected")) {
                  selectedSwatch.classList.remove("swatch_selected");
                  if (swatchVal) swatchVal.value = "";

                  const input = selectedSwatch.querySelector("input");
                  if (input) {
                    this._scheduleHulkDeselectEvent(set, input);
                  }
                }
              });
            }
          });
      }
    });
  }

  /**
   * Hulk braucht ein change-Event, um seinen internen Preis nach dem Abwählen
   * zurückzusetzen. Das Event trägt die Farbe aber wieder als Auswahl ein,
   * wenn es nach einer neuen Auswahl feuert - dann landet die alte Farbe in
   * der Cart-Property. Darum pro Gruppe nur ein offener Timer, und vor dem
   * Feuern prüfen, dass die Gruppe wirklich noch leer ist.
   */
  _scheduleHulkDeselectEvent(optionSet, input) {
    this._cancelHulkDeselectEvent(optionSet);

    const timer = setTimeout(() => {
      this._pendingHulkDeselects?.delete(optionSet);

      const hasNewSelection =
        !!optionSet.querySelector(".swatch_selected") ||
        !!optionSet.querySelector(".hulk_swatch_hidden_prop")?.value;
      if (hasNewSelection || input.checked) return;

      input.dispatchEvent(
        new Event("change", { bubbles: true, cancelable: true })
      );
    }, 500);

    if (!this._pendingHulkDeselects) this._pendingHulkDeselects = new Map();
    this._pendingHulkDeselects.set(optionSet, timer);
  }

  _cancelHulkDeselectEvent(optionSet) {
    const timer = this._pendingHulkDeselects?.get(optionSet);
    if (timer === undefined) return;

    clearTimeout(timer);
    this._pendingHulkDeselects.delete(optionSet);
  }

  isContainerFilled(container) {
    return this._isSlotFilled(container);
  }

  updateSelectionStatus() {
    const containers = this._getServerSlots();
    let filledCount = 0;

    containers.forEach((container) => {
      if (this.isContainerFilled(container)) {
        filledCount++;
      }
    });

    const statusElement = this.querySelector("#selection-status");
    if (statusElement) {
      statusElement.textContent = `${filledCount} von ${containers.length} ausgewählt`;
    }

    this._syncSetProgressCopy();
  }

  /**
   * Nummeriert die Pflicht-Slots nach ihrer Grid-Position durch.
   *
   * Bonus-Slots bleiben ausgenommen: ihre option-N-Klasse zeigt auf die
   * zugehörige optionale Hulk-Gruppe, nicht auf die Position im Grid. Ein
   * Umnummerieren würde die Auswahl in eine fremde (Pflicht-)Gruppe schreiben.
   */
  updateOptionIndexes() {
    const containers = Array.from(
      this.querySelectorAll(
        ".custom-options-enterprise .option:not([data-new-container])"
      )
    );

    containers.forEach((container, newIndex) => {
      // Remove old option- classes
      container.classList.forEach((cls) => {
        if (cls.startsWith("option-")) container.classList.remove(cls);
      });
      container.classList.add(`option-${newIndex + 1}`);

      const designEl = container.querySelector(".design");
      if (designEl) {
        designEl.classList.forEach((cls) => {
          if (cls.startsWith("design-")) designEl.classList.remove(cls);
        });
        designEl.classList.add(`design-${newIndex + 1}`);
      }

      const closeBtnEl = container.querySelector(".deleteOption");
      if (closeBtnEl) {
        closeBtnEl.classList.forEach((cls) => {
          if (cls.startsWith("design-")) closeBtnEl.classList.remove(cls);
        });
        closeBtnEl.classList.add(`design-${newIndex + 1}`);
      }
    });
  }

  updateButtonVisibility() {
    const options = this.querySelectorAll(".custom-options-enterprise .option");

    options.forEach((option) => {
      const design = option.querySelector(".design");
      const clearDesign = option.querySelector(".deleteOption");

      if (design && clearDesign) {
        const hasImage = design.querySelector("img") !== null;
        clearDesign.style.display = hasImage ? "block" : "none";
      }
    });

    // MutationObserver für .custom-options-enterprise - mit begrenztem Scope
    if (!this.buttonObserver) {
      const targetNode = this.querySelector(".custom-options-enterprise");
      if (targetNode) {
        this.buttonObserver = new MutationObserver(() =>
          this.updateButtonVisibility()
        );
        this.buttonObserver.observe(targetNode, {
          childList: true,
          subtree: true,
        });
      }
    }
  }

  checkMatchingOpt() {
    const hulkOptions = document.querySelector(
      '[id^="hulkapps_custom_options_"]'
    );
    const imgContainer = this.getDrawerTitleEl();
    const hulkInputWrapper = this.querySelector(".hulk-input-wrapper");
    const topSection = this.querySelector(".top-section");
    const lowerBar = this.querySelector(".lower-bar");
    const lowerSection = this.querySelector(".lower-section");

    if (hulkOptions) hulkOptions.style.display = "block";
    if (imgContainer) {
      const sideDrawer = this.querySelector("#side-drawer");
      if (sideDrawer?.classList.contains("open")) {
        this._updateDrawerPickerHeader();
      } else {
        imgContainer.textContent = this.getDrawerHeaderTitle();
      }
    }
    if (hulkInputWrapper) hulkInputWrapper.style.display = "flex";
    if (topSection) topSection.style.display = "none";
    if (lowerBar) lowerBar.style.display = "none";
    if (lowerSection) lowerSection.style.display = "none";

    this.querySelectorAll(".option").forEach((opt) =>
      opt.classList.remove("active")
    );

    const designs = this.querySelectorAll(
      ".custom-options-enterprise .option .design"
    );
    designs.forEach((design) => {
      if (design.dataset.designClickListenerAttached) return;
      design.dataset.designClickListenerAttached = "true";
      design.addEventListener("click", () => this.handleDesignClick(design));
    });
  }

  handleDesignClick(design) {
    const clickedOption = design.closest(".option");
    const imgElement = design.querySelector("img");

    if (!imgElement && clickedOption) {
      setTimeout(() => clickedOption.classList.add("active"), 200);
    }

    this.updateButtonVisibility();
  }

  /**
   * Bonus-Slot ("Optionale Farbe"). `slotClass` muss die option-N-Klasse der
   * zugehörigen optionalen Hulk-Gruppe sein - darüber spiegelt
   * _commitHulkSwatchSelectionForSlot die Auswahl in die richtige Gruppe.
   *
   * `data-original-text` ist Pflicht: _isSlotFilled vergleicht den sichtbaren
   * Text damit und würde den Slot sonst als bereits gefüllt einstufen.
   */
  createNewContainer(slotClass, displayIndex) {
    const newContainer = document.createElement("div");
    newContainer.classList.add("option", "discount-farbe");
    if (slotClass) newContainer.classList.add(slotClass);
    newContainer.setAttribute("data-new-container", "true");
    newContainer.setAttribute("data-count", String(displayIndex || ""));

    const nextCount = this._getRequiredSlotCount() + (displayIndex || 1);
    const unit = this._getOptionalUnitPrices(nextCount);
    const discountPercent =
      unit?.percent || Math.round(GDN_OPTIONAL_COLOR_DISCOUNT * 100);
    const discountLabel = `-${discountPercent}%`;

    newContainer.innerHTML = `
      <div class="container-label">${discountLabel}</div>
      <div class="design"></div>
      <p data-original-text="${GDN_OPTIONAL_SLOT_LABEL}">${GDN_OPTIONAL_SLOT_LABEL}</p>
      <div class="gdn-optional-slot-price"></div>
      <div class="deleteOption" style="display: none;">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </div>
    `;

    const closeBtn = newContainer.querySelector(".deleteOption");
    closeBtn.addEventListener("click", (e) =>
      this.handleCloseClick(e, closeBtn)
    );

    return newContainer;
  }

  /**
   * Hält genau einen leeren Bonus-Slot am Ende vor, sobald alle Pflicht-Slots
   * gefüllt sind und noch optionale Hulk-Gruppen frei sind. Idempotent: ein
   * passender leerer Slot bleibt stehen, damit Auswahl und Event-Listener
   * nicht bei jedem Aufruf verloren gehen.
   */
  _syncOptionalSlots() {
    const enterprise = this.querySelector(".custom-options-enterprise");
    if (!enterprise) return;

    const groups = this._getOptionalHulkGroups();
    const filledCount = this._getFilledOptionalSlots().length;
    const capacity = this._getOptionalCapacity();

    const canOfferMore =
      this._areAllRequiredSlotsFilled() &&
      groups.length > 0 &&
      filledCount < capacity;

    const expectedClass = canOfferMore
      ? this._getSlotClassFromElement(groups[filledCount])
      : null;

    const emptySlots = this._getOptionalSlots().filter(
      (slot) => !this._isSlotFilled(slot)
    );
    const keeper = expectedClass
      ? emptySlots.find((slot) => slot.classList.contains(expectedClass))
      : null;

    emptySlots.forEach((slot) => {
      if (slot === keeper) return;
      if (this._pendingSlot === slot) this._pendingSlot = null;
      slot.remove();
    });

    if (canOfferMore && !keeper) {
      enterprise.appendChild(
        this.createNewContainer(expectedClass, filledCount + 1)
      );
    }

    this._syncOptionalHulkGroupState();
    this._syncColorCountOption();
    this._syncOptionalSlotPrices();
    this._syncAddToCartPrice();
    this.getCurrentActiveCont();
  }

  /**
   * Entfernt alle Bonus-Slots und räumt ihre Hulk-Properties ab. Nötig beim
   * Variant-Wechsel und beim Init, damit keine verwaisten Zusatzfarben im
   * Cart landen.
   */
  _resetOptionalSlots() {
    this._getOptionalSlots().forEach((slot) => {
      if (this._pendingSlot === slot) this._pendingSlot = null;
      if (this._isSlotFilled(slot)) {
        this._clearSelectionForContainer(slot);
      }
      slot.remove();
    });

    this._syncOptionalHulkGroupState();
  }

  checkAndAppendContainers() {
    this._syncOptionalSlots();

    const designBanner = this.querySelector("#designBanner");
    const options = this.querySelectorAll(
      ".custom-options-enterprise .option:not([data-new-container])"
    );
    const lastContainer = options[options.length - 1];
    const lastContainerHasImage = lastContainer?.querySelector(".design img");

    if (
      designBanner &&
      lastContainerHasImage &&
      (!designBanner.style.display || designBanner.style.display === "none")
    ) {
      setTimeout(() => designBanner.classList.add("show"), 500);
    }
  }

  /**
   * Markiert visuell den aktiven Slot mit schwarzem Rahmen und weißem Hintergrund
   * ("next-container"-Style).
   *
   * Auswahl-Reihenfolge:
   * 1. Erster leerer Ziel-Slot — Pflicht zuerst, danach der leere Bonus-Slot
   *    ("Optionale Farbe"), sobald das Set voll ist.
   * 2. Wenn alle Slots gefüllt sind: der aktive Slot (.option.active),
   *    den der User für gezielten Austausch markiert hat.
   * 3. Sonst kein Rahmen.
   */
  getCurrentActiveCont() {
    const containers = this.querySelectorAll(
      ".custom-options-enterprise .option"
    );

    let activeContainer = this._getFirstEmptyTargetSlot();
    if (!activeContainer) {
      activeContainer = Array.from(containers).find((c) =>
        c.classList.contains("active")
      );
    }

    containers.forEach((container) => {
      const design = container.querySelector(".design");
      const img = design?.querySelector("img");

      if (design) {
        design.style.border = "";
        design.style.backgroundColor = "#f1edea";
        design.classList.remove("next-container");

        if (img) {
          design.style.backgroundImage = "none";
        } else {
          design.style.backgroundImage =
            "url('https://cdn.shopify.com/s/files/1/0832/5008/6221/files/new-nail-bg.webp?v=1739014172')";
        }
      }
    });

    if (activeContainer) {
      const getActiveDesign = activeContainer.querySelector(".design");
      if (getActiveDesign) {
        getActiveDesign.style.border = "1px solid black";
        getActiveDesign.style.backgroundColor = "transparent";
        getActiveDesign.classList.add("next-container");
      }
    }
  }

  updateBannerMessage() {
    const bannerText = this.querySelector("#bannerText");
    const bannerParent = this.querySelector("#designBanner");
    const bannerVarText = this.querySelector("#bannerVariantText");
    const addToCartButton = document.querySelector(
      ".product-form__payment-container button#AddToCart"
    );
    const stickyAddToCartButton = document.querySelector(
      ".product-sticky-form__form button#StickyAddToCart"
    );
    const nextContainerButton = document.getElementById("nextContainerButton");
    const stickyNextContainerButton = document.getElementById(
      "sticky_nextContainerButton"
    );
    const paymentButtons = document.querySelector(".shopify-payment-button");

    const existingContainers = this.querySelectorAll(
      ".custom-options-enterprise .option:not([data-new-container])"
    );
    const existingContainersWithImages = Array.from(existingContainers).filter(
      (opt) => opt.querySelector(".design img") !== null
    ).length;

    const colorName = this._getBannerSuccessColorName();
    const { nextPickBanner } = this.getSlotConfig();
    if (colorName && bannerVarText) {
      bannerVarText.textContent = `${colorName} erfolgreich ausgewählt`;
    } else if (bannerVarText && existingContainersWithImages === 0) {
      bannerVarText.textContent = nextPickBanner;
    }

    if (existingContainersWithImages === existingContainers.length) {
      setTimeout(() => bannerParent?.classList.add("show"), 500);
      if (bannerText)
        bannerText.textContent = "Toller Look! Dein Set ist jetzt bereit!";
      if (addToCartButton) addToCartButton.style.display = "block";
      if (paymentButtons) paymentButtons.style.display = "block";
      if (stickyAddToCartButton) stickyAddToCartButton.style.display = "block";
      if (stickyNextContainerButton)
        stickyNextContainerButton.style.display = "none";
      if (nextContainerButton) nextContainerButton.style.display = "none";
    } else if (existingContainersWithImages >= 1) {
      if (bannerText) bannerText.textContent = nextPickBanner;
      setTimeout(() => bannerParent?.classList.add("show"), 500);
      if (addToCartButton) addToCartButton.style.display = "none";
      if (paymentButtons) paymentButtons.style.display = "none";
      if (stickyAddToCartButton) stickyAddToCartButton.style.display = "none";
      if (stickyNextContainerButton)
        stickyNextContainerButton.style.display = "block";
      if (nextContainerButton) nextContainerButton.style.display = "block";
    } else {
      setTimeout(() => bannerParent?.classList.remove("show"), 500);
      if (addToCartButton) addToCartButton.style.display = "none";
      if (paymentButtons) paymentButtons.style.display = "none";
      if (stickyAddToCartButton) stickyAddToCartButton.style.display = "none";
      if (stickyNextContainerButton)
        stickyNextContainerButton.style.display = "block";
      if (nextContainerButton) nextContainerButton.style.display = "block";
    }
  }

  getDataCount() {
    setTimeout(() => {
      const { chooseButtonPrefix } = this.getSlotConfig();
      document.querySelectorAll(".hulk-custom").forEach((btn) => {
        btn.textContent = chooseButtonPrefix;
      });
    }, 100);
  }

  createNextContainerButton() {
    // Buttons werden jetzt serverseitig in product-form.liquid gerendert
    // Hier nur Event-Listener hinzufügen wenn sie noch nicht vorhanden sind
    const nextContainerButton = document.getElementById("nextContainerButton");
    const stickyNextContainerButton = document.getElementById(
      "sticky_nextContainerButton"
    );

    if (nextContainerButton && !nextContainerButton.dataset.listenerAttached) {
      nextContainerButton.dataset.listenerAttached = "true";
      nextContainerButton.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.openNextContainerWithoutImage();
      });
    }

    if (
      stickyNextContainerButton &&
      !stickyNextContainerButton.dataset.listenerAttached
    ) {
      stickyNextContainerButton.dataset.listenerAttached = "true";
      stickyNextContainerButton.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.openNextContainerWithoutImage();
      });
    }
  }

  openNextContainerWithoutImage() {
    const nextContainer = this._getFirstEmptyTargetSlot();
    if (nextContainer) {
      this.setActiveSlot(nextContainer);
      this._openDrawerForPicker();
    }
  }

  setupSwatchListeners() {
    const swatchOptions = document.querySelectorAll(".hulkapps_swatch_option");

    swatchOptions.forEach((swatchOption) => {
      // Prüfen ob bereits Listener vorhanden
      if (swatchOption.dataset.listenerAttached) return;
      swatchOption.dataset.listenerAttached = "true";

      swatchOption.addEventListener(
        "click",
        (event) => {
          if (event.target.closest(".gdn-swatch-qty-overlay")) return;
          this.handleSwatchClick(swatchOption, event);
        },
        true
      );
    });

    // Option Click Handler
    this.querySelectorAll(".option").forEach((option) => {
      if (option.dataset.clickListenerAttached) return;
      option.dataset.clickListenerAttached = "true";

      option.addEventListener("click", () => this.handleOptionClick(option));
    });
  }

  handleSwatchClick(swatchOption, event) {
    const preview = swatchOption.querySelector(".swatch-preview");
    if (preview?.dataset.swipeHandled === "true") return;

    if (event?.target?.closest(".gdn-swatch-qty-btn")) return;

    const sideDrawer = this.querySelector("#side-drawer");
    if (!sideDrawer?.classList.contains("open")) return;

    // Label-Activation unterbinden: sonst schickt der Browser nach diesem
    // Handler einen zweiten, synthetischen Klick an das erste labelbare
    // Kind des <label> - das füllt einen weiteren Slot oder entfernt die
    // gerade getroffene Auswahl wieder.
    event?.preventDefault?.();

    this._ensurePickerTargetSlot();
    const targetSlot = this._resolvePickerTargetSlot();
    if (!targetSlot) return;

    const added = this._addSwatchToSlot(targetSlot, swatchOption);
    if (!added) return;
  }

  /**
   * Markiert den Slot, der bei der nächsten Swatch-Auswahl gefüllt/ersetzt wird.
   *
   * Auswahl-Regel:
   * - Klick auf einen GEFÜLLTEN Slot → genau dieser Slot wird Target
   *   (gezielter Austausch der bereits gewählten Farbe).
   * - Klick auf einen LEEREN Slot → der NÄCHSTE LEERE Server-Slot wird Target
   *   (sequenzielles Befüllen von Slot 1, 2, 3 ... egal auf welchen leeren
   *   Slot der User klickt).
   *
   * Bonus-Slots ("Optionale Farbe") kommen bei der "next empty"-Suche immer
   * zuletzt (_getFirstEmptyTargetSlot). Andernfalls würde der leere Bonus-Slot
   * den Auswahl-Pointer stehlen und Pflicht-Slots wären nicht mehr
   * austauschbar.
   *
   * `_pendingSlot` ist die zuverlässige Referenz für handleSwatchClick und
   * überlebt CSS-Klassen-Resets (z.B. checkMatchingOpt entfernt regelmäßig
   * alle .active-Klassen). `.option.active` bleibt zusätzlich für bestehendes
   * CSS / visuelles Feedback.
   */
  setActiveSlot(clickedOption) {
    this.querySelectorAll(".option").forEach((opt) =>
      opt.classList.remove("active")
    );

    let target = null;
    if (clickedOption) {
      const clickedIsFilled = this._isSlotFilled(clickedOption);
      if (clickedIsFilled) {
        target = clickedOption;
      } else {
        target = this._getFirstEmptyTargetSlot() || clickedOption;
      }
    }

    this._pendingSlot = target;

    if (target) {
      target.classList.add("active");
    }

    this.getCurrentActiveCont();
  }

  handleOptionClick(option) {
    this.setActiveSlot(option);

    this.updateBannerMessage();
    this.updateSelectionStatus();
    this.updateButtonVisibility();
    this.checkAndAppendContainers();
  }

  setupDrawer() {
    const sideDrawer = this.querySelector("#side-drawer");

    if (!sideDrawer) return;

    if (!this._drawerDesignClickBound) {
      this._drawerDesignClickBound = true;
      this.addEventListener("click", (event) => {
        const design = event.target.closest(
          ".custom-options-enterprise .option .design"
        );
        if (!design || !this.contains(design)) return;

        const clickedOption = design.closest(".option");
        if (clickedOption) {
          this.setActiveSlot(clickedOption);
        }

        event.preventDefault();
        event.stopPropagation();

        if (sideDrawer.classList.contains("open")) {
          this.closeDrawer();
          return;
        }

        this._openDrawerForPicker();
      });
    }

    // Click outside to close (nur einmal binden)
    if (!this._drawerOutsideClickBound) {
      this._drawerOutsideClickBound = true;
      document.addEventListener("click", (event) => {
        if (
          sideDrawer.classList.contains("open") &&
          !sideDrawer.contains(event.target)
        ) {
          const isDesignClick = event.target.closest(".design");
          if (!isDesignClick) {
            this.closeDrawer();
          }
        }
      });
    }
  }

  closeDrawer() {
    const sideDrawer = this.querySelector("#side-drawer");

    sideDrawer?.classList.remove("open");
    this._syncDrawerScrollLock(false);
    this._syncBlurOverlay(false);
    this._hideLoader();
    this._resetDrawerScroll();
    this._syncDrawerFooter();

    this.querySelectorAll(".option").forEach((opt) =>
      opt.classList.remove("active")
    );
    this._pendingSlot = null;

    this.updateSelectionStatus();
    this.updateButtonVisibility();
    this.updateBannerMessage();
    this.createCustomContainerLabels();
    this.getDataCount();
  }

  /**
   * Sammelt Variant-Infos aus dem Hulk-DOM.
   * Pro Swatch-Label findet sich ein <input data-variant-id data-image
   * data-conditional-value> und ein .hulkapps-tooltip.
   *
   * Returns Array<{label, tooltip, colorName, variantId, hulkImage}>.
   */
  _collectSwatchInfo() {
    const labels = document.querySelectorAll(".hulkapps_swatch_option");
    const result = [];

    labels.forEach((label) => {
      const tooltip = label.querySelector(".hulkapps-tooltip");
      if (!tooltip) return;

      const input = label.querySelector("input[data-variant-id]");
      const variantId = input?.dataset.variantId || null;
      const colorName =
        input?.dataset.conditionalValue ||
        tooltip.textContent.trim() ||
        label.getAttribute("aria-label")?.trim() ||
        "";
      const hulkImage =
        input?.dataset.image ||
        this.parsedColorOptions?.swatchImages?.[colorName] ||
        null;

      result.push({ label, tooltip, colorName, variantId, hulkImage });
    });

    return result;
  }

  /**
   * Holt das gesamte Variant-ID -> Bilder Mapping aus der öffentlichen
   * Collection-JSON-API. Alle Farb-Collections werden parallel gefetcht
   * und zu einem einzigen Mapping zusammengeführt - ganz ohne Auth/Token.
   *
   * - Nutzt sessionStorage als Cache (TTL 24h).
   * - Bei Fehler einzelner Collections: wird übersprungen, andere laufen weiter.
   * - Bei Fehler: liefert {} zurück, das Karussell fällt auf das Hulk-Bild
   *   zurück (= 1-Slide-Verhalten).
   *
   * Returns: { byVariantId: { [variantId]: string[] }, byHandle: { [handle]: string[] } }
   */
  async fetchColorProductImages() {
    if (this._colorImagesCache) {
      return this._colorImagesCache;
    }

    if (this._colorImagesFetchPromise) {
      return this._colorImagesFetchPromise;
    }

    this._colorImagesFetchPromise = this._fetchColorProductImagesImpl();
    try {
      this._colorImagesCache = await this._colorImagesFetchPromise;
      return this._colorImagesCache;
    } finally {
      this._colorImagesFetchPromise = null;
    }
  }

  async _fetchColorProductImagesImpl() {
    const COLLECTION_HANDLES = [
      "shellac",
      "farbgele",
      "no-file-gel",
      "aufbaugele",
      "bottle-gel",
      "rubber-base",
      "base-top-coat-kopie",
      "top",
    ];
    const cacheKey = `gdn:colorImages:v5:all`;
    const cacheTTL = 24 * 60 * 60 * 1000;

    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.timestamp && Date.now() - parsed.timestamp < cacheTTL) {
          return parsed.data || {};
        }
      }
    } catch (e) {
      // sessionStorage nicht verfügbar oder JSON kaputt -> neu laden
    }

    const byVariantId = {};
    const byHandle = {};

    const results = await Promise.allSettled(
      COLLECTION_HANDLES.map((handle) =>
        fetch(`/collections/${handle}/products.json?limit=250`, {
          headers: { Accept: "application/json" },
        }).then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status} for ${handle}`);
          return r.json();
        })
      )
    );

    results.forEach((result, i) => {
      if (result.status === "rejected") {
        console.warn(
          `SetConfigurator: Collection "${COLLECTION_HANDLES[i]}" fetch failed`,
          result.reason
        );
        return;
      }
      const products = result.value.products || [];
      products.forEach((product) => {
        const imageUrls = (product.images || [])
          .map((img) => img && img.src)
          .filter(Boolean);
        if (imageUrls.length === 0) return;

        byHandle[product.handle] = imageUrls;

        (product.variants || []).forEach((variant) => {
          if (variant && variant.id) {
            byVariantId[String(variant.id)] = imageUrls;
          }
        });
      });
    });

    const mapping = { byVariantId, byHandle };

    try {
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({ timestamp: Date.now(), data: mapping })
      );
    } catch (e) {
      // sessionStorage voll -> trotzdem in-memory verfügbar
    }

    return mapping;
  }

  _normalizeColorImageCache(cache) {
    if (!cache || typeof cache !== "object") {
      return { byVariantId: {}, byHandle: {} };
    }
    if (cache.byVariantId || cache.byHandle) {
      return {
        byVariantId: cache.byVariantId || {},
        byHandle: cache.byHandle || {},
      };
    }
    return { byVariantId: cache, byHandle: {} };
  }

  _slugifyColorFragment(text) {
    return String(text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  _productHandleFromColorName(colorName) {
    const raw = String(colorName || "").trim();
    if (!raw) return null;

    const { slotSingular } = this.getSlotConfig();
    let colorPart = raw;

    const stripLeading = (prefix) => {
      const re = new RegExp(`^${prefix}\\s*`, "i");
      if (re.test(colorPart)) {
        colorPart = colorPart.replace(re, "").trim();
      }
    };

    if (/top\s*coat/i.test(slotSingular) || /^top\s*coat/i.test(raw)) {
      stripLeading("top coat");
      const slug = this._slugifyColorFragment(colorPart);
      return slug ? `top-coat-${slug}` : null;
    }

    if (/rubber\s*base/i.test(slotSingular) || /^rubber\s*base/i.test(raw)) {
      stripLeading("rubber base");
      return this._slugifyColorFragment(colorPart) || null;
    }

    if (/^shellac/i.test(raw)) {
      stripLeading("shellac");
      return this._slugifyColorFragment(colorPart) || null;
    }

    if (/no\s*file\s*gel/i.test(raw)) {
      stripLeading("no file gel");
      const slug = this._slugifyColorFragment(colorPart);
      return slug ? `no-file-gel-${slug}` : null;
    }

    if (/^farbgel/i.test(raw)) {
      stripLeading("farbgel");
      const slug = this._slugifyColorFragment(colorPart);
      return slug ? `farbgel-${slug}` : null;
    }

    if (this._isFarbgelBundle()) {
      const slug = this._slugifyColorFragment(
        raw.replace(/^farbgel\s+/i, "").trim()
      );
      if (slug) return slug;
    }

    return null;
  }

  _isFarbgelBundle() {
    if (this.productHandle === "3er-bundle") return true;
    return /\/products\/3er-bundle(?:\/|$|\?)/i.test(
      window.location.pathname + window.location.search
    );
  }

  _resolveProductHandleCandidates(colorName) {
    const candidates = [];
    const fromName = this._productHandleFromColorName(colorName);
    if (fromName) candidates.push(fromName);

    if (this._isFarbgelBundle()) {
      const raw = String(colorName || "").trim();
      const slug = this._slugifyColorFragment(
        raw.replace(/^farbgel\s+/i, "").trim()
      );
      if (slug) {
        candidates.push(slug);
        candidates.push(`farbgel-${slug}`);
      }
    }

    return [...new Set(candidates.filter(Boolean))];
  }

  _getProductImages(cache, variantId, colorName) {
    const { byVariantId, byHandle } = this._normalizeColorImageCache(cache);
    let images =
      variantId != null ? byVariantId[String(variantId)] : undefined;

    if (!images || images.length <= 1) {
      for (const handle of this._resolveProductHandleCandidates(colorName)) {
        if (byHandle[handle]?.length) {
          images = byHandle[handle];
          break;
        }
      }
    }

    return images || [];
  }

  _isVideoMedia(url) {
    if (!url || typeof url !== "string") return false;
    return (
      /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url) || /\/videos\//i.test(url)
    );
  }

  _getCarouselProductImages(cache, variantId, colorName) {
    return this._getProductImages(cache, variantId, colorName).filter(
      (url) => !this._isVideoMedia(url)
    );
  }

  _resolveHulkSwatchImage(label, colorName, tooltip) {
    const radioInput = label.querySelector(
      ".swatch_radio, input[name^='hulk_swatch']"
    );
    const variantHolder =
      label.querySelector("[data-variant-id]") ||
      label.querySelector(".hulk_po_radio");
    const tooltipImg = tooltip?.querySelector("img");

    return (
      radioInput?.dataset?.image ||
      variantHolder?.dataset?.image ||
      tooltipImg?.currentSrc ||
      tooltipImg?.src ||
      this.parsedColorOptions?.swatchImages?.[colorName] ||
      null
    );
  }

  /**
   * Max. 3 Slides: 1 = Hulk-Bild, 2+3 = 2. und 3. Storefront-Bild (Index 1+2).
   */
  _buildPreviewSlideSrcs(hulkImage, productImages) {
    const slideSrcs = [];
    const seen = new Set();

    const add = (url) => {
      if (!url || typeof url !== "string" || slideSrcs.length >= 3) return;
      const key = url.split("?")[0];
      if (seen.has(key)) return;
      seen.add(key);
      slideSrcs.push(this._resizeShopifyImage(url, 600));
    };

    if (hulkImage) add(hulkImage);
    (productImages || []).slice(1, 3).forEach(add);

    if (slideSrcs.length === 0 && productImages?.[0]) {
      add(productImages[0]);
    }

    return slideSrcs;
  }

  /**
   * Liefert eine resized + zentriert quadratisch zugeschnittene Variante
   * einer Shopify-CDN-URL.
   *
   * Crop-Center sorgt dafür, dass Hochformat-Produktbilder (Finger, Hand)
   * und Querformat-Hulk-Bilder beide als Quadrat dargestellt werden, also
   * im 200×200 Cell-Layout gleich aussehen.
   *
   * Bei Nicht-Shopify-URLs (oder fehlerhaften Inputs) wird die URL
   * unverändert zurückgegeben - das Karussell zeigt dann das Original.
   */
  _resizeShopifyImage(url, size = 600) {
    if (!url || typeof url !== "string") return url;
    if (!url.includes("cdn.shopify.com") && !url.includes("/files/")) {
      return url;
    }
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}width=${size}&height=${size}&crop=center`;
  }

  _mountSwatchPreview(label, preview) {
    const tooltip = label.querySelector(".hulkapps-tooltip");
    if (tooltip) {
      this._preserveHulkSwatchInputs(label);
      tooltip.parentNode.replaceChild(preview, tooltip);
    } else {
      label.prepend(preview);
    }
    this._normalizeDrawerSwatchLabelText(label);
  }

  _normalizeDrawerSwatchLabelText(label) {
    if (!label) return;

    label.querySelectorAll(":scope > div").forEach((div) => {
      if (
        div.classList.contains("swatch-preview") ||
        div.classList.contains("main-carousel") ||
        div.classList.contains("gdn-swatch-hulk-inputs")
      ) {
        return;
      }

      if (div.classList.contains("hulkapps-tooltip")) {
        div.style.setProperty("width", "100%", "important");
        div.style.setProperty("max-width", "100%", "important");
        div.style.setProperty("margin", "0", "important");
        div.style.setProperty("overflow", "hidden", "important");
        const inner = div.querySelector(".hulkapps-tooltip-inner");
        if (inner) {
          inner.style.setProperty("width", "100%", "important");
          inner.style.setProperty("max-width", "100%", "important");
        }
        return;
      }

      div.style.setProperty("display", "block", "important");
      div.style.setProperty("width", "100%", "important");
      div.style.setProperty("max-width", "100%", "important");
      div.style.setProperty("margin", "0", "important");
      div.style.setProperty("text-align", "center", "important");
      div.style.setProperty("line-height", "1.2", "important");
      div.style.setProperty("white-space", "normal", "important");
      div.style.setProperty("overflow-wrap", "anywhere", "important");
      div.style.setProperty("word-break", "break-word", "important");
      div.style.setProperty("overflow", "hidden", "important");
    });
  }

  _preserveHulkSwatchInputs(label) {
    if (!label) return;

    let host = label.querySelector(".gdn-swatch-hulk-inputs");
    if (!host) {
      host = document.createElement("div");
      host.className = "gdn-swatch-hulk-inputs";
      host.hidden = true;
      host.setAttribute("aria-hidden", "true");
      label.appendChild(host);
    }

    label
      .querySelectorAll(
        "input.swatch_radio, input[name^='hulk_swatch'], .hulk_po_radio"
      )
      .forEach((el) => {
        if (host.contains(el)) return;
        el.style.display = "none";
        host.appendChild(el);
      });
  }

  _getHulkPropertyValue(swatchOption) {
    const radioInput = swatchOption.querySelector(
      ".swatch_radio, input[name^='hulk_swatch']"
    );
    const radioDiv = swatchOption.querySelector(".hulk_po_radio");

    if (radioInput?.value && radioInput.value !== "undefined") {
      return radioInput.value;
    }

    const conditionalValue =
      radioDiv?.dataset?.conditionalValue ||
      radioInput?.dataset?.conditionalValue ||
      swatchOption.dataset.swatchLabel ||
      "";

    if (!conditionalValue) return "";

    const ariaLabel = swatchOption.getAttribute("aria-label") || "";
    const priceMatch = ariaLabel.match(/\[\s*€[\d.,]+\s*\]/);
    return priceMatch
      ? `${conditionalValue} ${priceMatch[0]}`.trim()
      : conditionalValue;
  }

  _syncHulkHiddenProperty(swatchOption, explicitValue) {
    const optionSet = swatchOption.closest(".hulkapps_option");
    const hiddenProp = optionSet?.querySelector(".hulk_swatch_hidden_prop");
    if (!hiddenProp) return;

    const value = explicitValue || this._getHulkPropertyValue(swatchOption);
    if (!value || value === "undefined") return;

    hiddenProp.value = value;
    hiddenProp.disabled = false;
    hiddenProp.dispatchEvent(new Event("input", { bubbles: true }));
    hiddenProp.dispatchEvent(new Event("change", { bubbles: true }));
  }

  _getSwatchSelectionData(swatchOption) {
    const radioInput = swatchOption.querySelector(
      ".swatch_radio, input[name^='hulk_swatch']"
    );
    const variantHolder =
      swatchOption.querySelector("[data-variant-id]") ||
      swatchOption.querySelector(".hulk_po_radio");
    const preview = swatchOption.querySelector(".swatch-preview");
    const tooltip = swatchOption.querySelector(".hulkapps-tooltip");

    const ariaLabelValue = this.cleanPriceText(
      swatchOption.dataset.swatchLabel ||
        variantHolder?.dataset?.conditionalValue ||
        swatchOption.getAttribute("aria-label") ||
        ""
    );

    let imgSrc =
      radioInput?.dataset?.image ||
      variantHolder?.dataset?.image ||
      swatchOption.dataset.swatchImage ||
      "";

    if (!imgSrc) {
      imgSrc =
        this._resolveHulkSwatchImage(
          swatchOption,
          ariaLabelValue,
          tooltip
        ) || "";
    }

    if (!imgSrc && preview?.dataset.slides) {
      try {
        const slides = JSON.parse(preview.dataset.slides);
        imgSrc = slides[0] || "";
      } catch {
        // ignore invalid slide JSON
      }
    }

    if (!imgSrc) {
      const previewImg = preview?.querySelector("img");
      imgSrc = previewImg?.currentSrc || previewImg?.src || "";
    }

    return { imgSrc, ariaLabelValue };
  }

  _commitHulkSwatchSelection(swatchOption) {
    const optionSet = swatchOption.closest(".hulkapps_option");
    const radioInput = swatchOption.querySelector(
      ".swatch_radio, input[name^='hulk_swatch']"
    );
    const radioDiv = swatchOption.querySelector(".hulk_po_radio");

    if (optionSet) this._cancelHulkDeselectEvent(optionSet);

    optionSet?.querySelectorAll(".swatch_selected").forEach((el) => {
      el.classList.remove("swatch_selected");
    });
    optionSet
      ?.querySelectorAll(".swatch_radio, input[name^='hulk_swatch']")
      .forEach((input) => {
        input.checked = false;
      });

    if (radioInput) {
      radioInput.checked = true;
      radioInput.disabled = false;
      radioInput.dispatchEvent(new Event("input", { bubbles: true }));
      radioInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    if (radioDiv) {
      radioDiv.classList.add("swatch_selected");
    }

    this._syncHulkHiddenProperty(swatchOption);
  }

  /**
   * Schreibt vor dem Submit für jeden gefüllten Slot die Hulk-Property nach.
   * Schließt Bonus-Slots ein - ohne sie fehlen die Zusatzfarben im Warenkorb.
   */
  _syncAllHulkHiddenPropertiesFromChooser() {
    this._getAllSelectableSlots().forEach((slot) => {
      const slotClass = Array.from(slot.classList).find((cls) =>
        cls.startsWith("option-")
      );
      if (!slotClass || !slot.querySelector(".design img")) return;

      const slotName = this.cleanPriceText(
        slot.querySelector(":scope > p")?.textContent || ""
      );
      if (!slotName) return;

      const hulkSet = document.querySelector(
        `.hulkapps_option.swatch_render.${slotClass}`
      );
      if (!hulkSet) return;

      const selectedSwatch = hulkSet.querySelector(
        ".hulkapps_swatch_option .swatch_selected"
      )?.closest(".hulkapps_swatch_option");

      if (selectedSwatch) {
        this._syncHulkHiddenProperty(selectedSwatch);
        return;
      }

      const matchingSwatch = Array.from(
        hulkSet.querySelectorAll(".hulkapps_swatch_option")
      ).find((label) => {
        const labelName = this.cleanPriceText(
          label.dataset.swatchLabel ||
            label.getAttribute("aria-label") ||
            label.querySelector(".hulk_po_radio")?.dataset?.conditionalValue ||
            ""
        );
        return labelName === slotName;
      });

      if (matchingSwatch) {
        this._syncHulkHiddenProperty(matchingSwatch);
        return;
      }

      const hiddenProp = hulkSet.querySelector(".hulk_swatch_hidden_prop");
      if (
        hiddenProp &&
        (!hiddenProp.value || hiddenProp.value === "undefined")
      ) {
        hiddenProp.value = slotName;
        hiddenProp.disabled = false;
      }
    });

    // Letzte Instanz vor dem Submit: ungenutzte optionale Gruppen wieder
    // stilllegen, damit keine leeren "Optionale Farbe"-Properties mitgehen.
    this._syncOptionalHulkGroupState();
  }

  _showSwatchPreviewSlide(preview, slideSrcs, index) {
    const img = preview?.querySelector("img");
    if (!preview || !img || !slideSrcs[index]) return;

    preview.dataset.activeSlide = String(index);
    img.src = slideSrcs[index];
    preview.querySelectorAll(".swatch-preview-dot").forEach((dot, dotIndex) => {
      dot.classList.toggle("is-selected", dotIndex === index);
      dot.setAttribute("aria-current", dotIndex === index ? "true" : "false");
    });
  }

  _getSwatchPreviewDefaultSlide(preview) {
    return Number(preview?.dataset.pinnedSlide || 0);
  }

  _resetSwatchPreviewToDefault(preview) {
    if (!preview) return;

    let slideSrcs = [];
    try {
      slideSrcs = JSON.parse(preview.dataset.slides || "[]");
    } catch {
      return;
    }

    this._showSwatchPreviewSlide(
      preview,
      slideSrcs,
      this._getSwatchPreviewDefaultSlide(preview)
    );
  }

  _setupSwatchPreviewNavigation() {
    if (this._swatchPreviewNavAttached) return;
    this._swatchPreviewNavAttached = true;

    const activateDot = (event) => {
      const dot = event.target.closest(".swatch-preview-dot");
      if (!dot || !this.contains(dot)) return;

      event.preventDefault();
      event.stopPropagation();

      const preview = dot.closest(".swatch-preview");
      if (!preview) return;

      let slideSrcs = [];
      try {
        slideSrcs = JSON.parse(preview.dataset.slides || "[]");
      } catch {
        return;
      }

      const index = Number(dot.dataset.index || 0);
      preview.dataset.pinnedSlide = String(index);
      this._showSwatchPreviewSlide(preview, slideSrcs, index);
    };

    this.addEventListener("click", activateDot, true);
    this.addEventListener("pointerup", activateDot, true);
    this._setupSwatchPreviewSwipe();
  }

  _setupSwatchPreviewSwipe() {
    if (this._swatchPreviewSwipeAttached) return;
    this._swatchPreviewSwipeAttached = true;

    let startX = 0;
    let startY = 0;
    let trackingPreview = null;

    const getPoint = (event) => {
      if (event.changedTouches?.length) {
        return event.changedTouches[0];
      }
      if (event.touches?.length) {
        return event.touches[0];
      }
      return event;
    };

    const onStart = (event) => {
      const preview = event.target.closest(".swatch-preview");
      if (!preview || !this.contains(preview)) return;

      const point = getPoint(event);
      trackingPreview = preview;
      startX = point.clientX;
      startY = point.clientY;
    };

    const onEnd = (event) => {
      if (!trackingPreview) return;

      const preview = trackingPreview;
      trackingPreview = null;

      const point = getPoint(event);
      const deltaX = point.clientX - startX;
      const deltaY = point.clientY - startY;

      if (Math.abs(deltaX) < 28 || Math.abs(deltaX) < Math.abs(deltaY)) return;

      let slideSrcs = [];
      try {
        slideSrcs = JSON.parse(preview.dataset.slides || "[]");
      } catch {
        return;
      }
      if (slideSrcs.length <= 1) return;

      event.preventDefault();
      event.stopPropagation();

      const current = Number(preview.dataset.activeSlide || 0);
      const next =
        deltaX < 0
          ? Math.min(current + 1, slideSrcs.length - 1)
          : Math.max(current - 1, 0);

      if (next === current) return;

      preview.dataset.pinnedSlide = String(next);
      this._showSwatchPreviewSlide(preview, slideSrcs, next);
      preview.dataset.swipeHandled = "true";
      window.setTimeout(() => {
        delete preview.dataset.swipeHandled;
      }, 400);
    };

    this.addEventListener("pointerdown", onStart, true);
    this.addEventListener("pointerup", onEnd, true);
    this.addEventListener("touchstart", onStart, { capture: true, passive: true });
    this.addEventListener("touchend", onEnd, { capture: true, passive: false });
  }

  _buildDrawerSwatchPreviews() {
    this._refreshDrawerSwatchObserver();
  }

  /**
   * Baut eine statische Swatch-Vorschau mit Dot-Navigation (ohne Flickity/Swipe).
   */
  async _buildCarouselForLabel(label) {
    if (
      label.dataset.carouselBuilt === "true" ||
      label.dataset.carouselBuilding === "true"
    ) {
      return;
    }
    label.dataset.carouselBuilding = "true";

    try {
      const tooltip = label.querySelector(".hulkapps-tooltip");
      if (!tooltip || !tooltip.parentNode) return;

      const variantHolder =
        label.querySelector("[data-variant-id]") ||
        (label.dataset.variantId ? label : null);
      const variantId = variantHolder?.dataset.variantId || null;

      const colorName = this.cleanPriceText(
        variantHolder?.dataset.conditionalValue ||
          tooltip.textContent.trim() ||
          label.getAttribute("aria-label")?.trim() ||
          ""
      );
      const hulkImage = this._resolveHulkSwatchImage(label, colorName, tooltip);

      const productImages = await this.fetchColorProductImages();
      const carouselProductImages = this._getCarouselProductImages(
        productImages,
        variantId,
        colorName
      );

      const slideSrcs = this._buildPreviewSlideSrcs(
        hulkImage,
        carouselProductImages
      );
      if (slideSrcs.length === 0) return;

      const preview = document.createElement("div");
      preview.classList.add("main-carousel", "swatch-preview");

      const img = document.createElement("img");
      img.src = slideSrcs[0];
      img.alt = colorName;
      img.draggable = false;
      preview.appendChild(img);
      preview.dataset.slides = JSON.stringify(slideSrcs);
      preview.dataset.activeSlide = "0";
      preview.dataset.pinnedSlide = "0";
      label.dataset.swatchImage = hulkImage || slideSrcs[0] || "";
      label.dataset.swatchLabel = colorName || "";
      this._mountSwatchPreview(label, preview);

      if (slideSrcs.length > 1) {
        const dots = document.createElement("ol");
        dots.classList.add("swatch-preview-dots");

        slideSrcs.forEach((_, index) => {
          const dot = document.createElement("button");
          dot.type = "button";
          dot.classList.add("swatch-preview-dot");
          dot.dataset.index = String(index);
          if (index === 0) {
            dot.classList.add("is-selected");
            dot.setAttribute("aria-current", "true");
          }
          dot.setAttribute("aria-label", `Bild ${index + 1}`);
          dots.appendChild(dot);
        });

        preview.appendChild(dots);

        label.addEventListener("mouseenter", () => {
          if (slideSrcs.length > 1) {
            this._showSwatchPreviewSlide(preview, slideSrcs, 1);
          }
        });
        label.addEventListener("mouseleave", () => {
          this._resetSwatchPreviewToDefault(preview);
        });
      }

      label.dataset.carouselBuilt = "true";
    } catch (error) {
      console.warn(
        "SetConfigurator: Swatch preview build failed for label",
        label,
        error
      );
    } finally {
      delete label.dataset.carouselBuilding;
      this._syncSwatchQtyOverlays();
    }
  }

  /**
   * Beobachtet Swatch-Labels via IntersectionObserver und baut Vorschauen
   * nur wenn sie im (oder nahe am) Viewport sind.
   */
  _setupCarouselObserver() {
    if (this._carouselObserver) return;

    this._carouselObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._buildCarouselForLabel(entry.target);
          }
        });
      },
      { rootMargin: "100px", threshold: 0.01 }
    );

    const observeLabels = () => {
      document.querySelectorAll(".hulkapps_swatch_option").forEach((label) => {
        if (label.dataset.carouselObserved === "true") return;
        label.dataset.carouselObserved = "true";
        this._carouselObserver.observe(label);
      });
    };

    observeLabels();

    const hulkContainer = document.querySelector(
      '[id^="hulkapps_custom_options_"]'
    );
    if (!hulkContainer || this._carouselMutationObserver) return;

    this._carouselMutationObserver = new MutationObserver(() => observeLabels());
    this._carouselMutationObserver.observe(hulkContainer, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Baut Swatch-Vorschauen on demand beim ersten Hover.
   */
  _setupCarouselHovers() {
    const handler = (event) => {
      const label = event.target.closest(".hulkapps_swatch_option");
      if (!label || label.dataset.carouselBuilt === "true") return;
      this._buildCarouselForLabel(label);
    };

    document.addEventListener("pointerover", handler, { passive: true });
    this._onCarouselPointerOver = handler;
  }

  async initSearch() {
    await this.loadFuse();
    if (!window.Fuse) return;

    const searchInput = this.querySelector(".hulk-input");
    const labels = document.querySelectorAll(".hulkapps_swatch_option");

    if (!searchInput || labels.length === 0) return;

    const options = Array.from(labels).map((label) => {
      const optionText =
        label.querySelector(".swatch_tooltip_title p")?.textContent.trim() ||
        "";
      return { text: optionText, label: label };
    });

    const fuse = new window.Fuse(options, {
      keys: ["text"],
      includeScore: true,
      threshold: 0.4,
    });

    searchInput.addEventListener("input", () => {
      const searchTerm = searchInput.value.trim().toLowerCase();

      if (searchTerm === "") {
        labels.forEach((label) => (label.style.display = ""));
        return;
      }

      const results = fuse.search(searchTerm);
      labels.forEach((label) => (label.style.display = "none"));

      results.forEach((result) => {
        result.item.label.style.display = "";
      });
    });
  }

  createCustomLabels() {
    if (!this.parsedColorOptions?.hulkOptions) return;

    try {
      const hulkOptions = this.parsedColorOptions.hulkOptions;

      for (const [key, value] of Object.entries(hulkOptions)) {
        document.querySelectorAll("label[aria-label]").forEach((label) => {
          const titleElement = label.querySelector(".swatch_tooltip_title p");
          if (titleElement) {
            const labelText = titleElement.textContent.trim();
            const textAtPosition = label.querySelector(".hulkapps-tooltip");

            if (key === labelText && textAtPosition) {
              const customLabel = document.createElement("div");
              customLabel.classList.add("hulk-label");
              customLabel.innerText = value;
              label.style.position = "relative";
              textAtPosition.appendChild(customLabel);
            }
          }
        });
      }
    } catch (error) {
      console.warn("SetConfigurator: Custom labels creation failed", error);
    }
  }

  createCustomContainerLabels() {
    if (!this.parsedColorOptions?.options) return;

    try {
      const badgeOptions = this.parsedColorOptions.options;

      badgeOptions.forEach((badge, index) => {
        const optionKey = `option-${index + 1}`;

        if (badge.hasOwnProperty(optionKey)) {
          const value = badge[optionKey];

          this.querySelectorAll(
            ".custom-options-enterprise .option:not([data-new-container])"
          ).forEach(
            (label) => {
              const optClasses = label.classList;
              optClasses.forEach((optClass) => {
                if (
                  optClass.startsWith("option-") ||
                  optClass.startsWith("discount-")
                ) {
                  const textAtPosition = label.querySelector(".design");
                  if (
                    optionKey === optClass &&
                    textAtPosition &&
                    !textAtPosition.querySelector(".container-label")
                  ) {
                    const customLabel = document.createElement("div");
                    customLabel.classList.add("container-label");
                    customLabel.innerText = value;
                    label.style.position = "relative";
                    textAtPosition.appendChild(customLabel);
                  }
                }
              });
            }
          );
        }
      });
    } catch (error) {
      console.warn("SetConfigurator: Container labels creation failed", error);
    }
  }

  initCustomFields() {
    if (!this.parsedCustomFields?.fields) return;

    const customFieldsContainer = document.querySelector(
      ".custom_options_fields"
    );
    if (!customFieldsContainer) return;

    customFieldsContainer.style.display = "block";

    try {
      const fieldOptions = this.parsedCustomFields.fields;
      let variantPointsHTML = "";

      if (
        fieldOptions.variant_points &&
        Array.isArray(fieldOptions.variant_points)
      ) {
        fieldOptions.variant_points.forEach((variantPoint) => {
          Object.keys(variantPoint).forEach((key) => {
            if (key.startsWith("text_line_") && variantPoint[key]) {
              variantPointsHTML += `
                <div class="in-bp">
                  <div class="bp-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 17 14" fill="none">
                      <path d="M1 9.57143L4.33333 13L16 1" stroke="#272727"></path>
                    </svg>
                  </div>
                  <div class="bp-text">
                    <span>${variantPoint[key]}</span>
                  </div>
                </div>
              `;
            }
          });
        });
      }

      const fieldHTML = `
        <div class="bullets-head">
          <span>${fieldOptions.title || ""}</span>
          <button class="whats-in-set bullets-question_mark">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </button>
        </div>
        <div class="starter-set-bp bp-grid">
          ${variantPointsHTML}
        </div>
      `;

      customFieldsContainer.insertAdjacentHTML("beforeend", fieldHTML);

      // What's in set button handler
      const whatsInSetBtn =
        customFieldsContainer.querySelector(".whats-in-set");
      if (whatsInSetBtn) {
        whatsInSetBtn.addEventListener("click", (event) =>
          this.handleWhatsInSetClick(event)
        );
      }
    } catch (error) {
      console.warn(
        "SetConfigurator: Custom fields initialization failed",
        error
      );
    }
  }

  handleWhatsInSetClick(event) {
    const sideDrawer = this.querySelector("#side-drawer");

    event.preventDefault();
    event.stopPropagation();
    sideDrawer?.classList.toggle("open");
    const isOpen = sideDrawer?.classList.contains("open");
    this._syncDrawerScrollLock(isOpen);
    this._syncBlurOverlay(isOpen);

    if (!isOpen) {
      this._syncDrawerFooter();
      return;
    }

    // Hide hulk options, show sections
    const hulkCustomOptions = document.querySelector(
      '[id^="hulkapps_custom_options_"]'
    );
    const hulkInputWrapper = this.querySelector(".hulk-input-wrapper");
    const topSection = this.querySelector(".top-section");
    const lowerBar = this.querySelector(".lower-bar");
    const lowerSection = this.querySelector(".lower-section");

    if (hulkCustomOptions) hulkCustomOptions.style.display = "none";
    if (hulkInputWrapper) hulkInputWrapper.style.display = "none";
    if (topSection) topSection.style.display = "flex";
    if (lowerBar) lowerBar.style.display = "flex";
    if (lowerSection) lowerSection.style.display = "flex";

    this._setDrawerFooterVisible(false);
    this.populateSideDrawerContent();
  }

  populateSideDrawerContent() {
    if (!this.parsedCustomFields?.fields?.side_drawer_content) return;

    try {
      const content = this.parsedCustomFields.fields.side_drawer_content;

      const topSection = this.querySelector(".top-section");
      const lowerSection = this.querySelector(".lower-section");
      const lowerSectionHeader = this.querySelector(".lower-bar");
      const topSectionHeader = this.getDrawerTitleEl();

      if (topSection) topSection.innerHTML = "";
      if (lowerSection) lowerSection.innerHTML = "";

      if (lowerSectionHeader && content.side_drawer_lower_section_title) {
        lowerSectionHeader.innerHTML = content.side_drawer_lower_section_title;
      }

      if (topSectionHeader && content.side_drawer_top_section_title) {
        topSectionHeader.textContent = this.normalizeDrawerTitle(
          content.side_drawer_top_section_title
        );
      }

      if (content.side_drawer_top_section?.[0]) {
        this.processSection(content.side_drawer_top_section[0], topSection);
      }

      if (content.side_drawer_lower_section?.[0]) {
        this.processSection(content.side_drawer_lower_section[0], lowerSection);
      }
    } catch (error) {
      console.warn(
        "SetConfigurator: Side drawer content population failed",
        error
      );
    }
  }

  processSection(sectionData, parentElement) {
    if (!parentElement) return;

    for (let key in sectionData) {
      const section = sectionData[key];

      if (section) {
        const span = document.createElement("span");

        if (section.image) {
          const img = document.createElement("img");
          img.src = section.image;
          img.alt = section.title || key;
          img.classList.add("section-img");
          span.appendChild(img);
        }

        if (section.title) {
          const title = document.createElement("h3");
          title.classList.add("sectionTitle");
          title.textContent = section.title;
          span.appendChild(title);
        }

        if (section.details) {
          const details = document.createElement("p");
          details.textContent = section.details;
          span.appendChild(details);
        }

        if (typeof section === "object" && !Array.isArray(section)) {
          this.processSection(section, span);
        }

        parentElement.appendChild(span);
      }
    }
  }

  _isEssentialsSet() {
    return (this.productHandle || "").includes("essentials-set");
  }

  _atcPriceEnabled() {
    return GDN_ATC_PRICE_ENABLED && this._isEssentialsSet();
  }

  /**
   * Preis im Warenkorb-Button. Vorerst nur Essentials: Hulk schreibt dort
   * nichts mehr, seit die optionalen Gruppen 0 € Aufpreis haben. Die Beträge
   * kommen aus dem gerenderten Preisblock, damit Währung und Checkout-Preis
   * identisch bleiben. Aus, solange GDN_ATC_PRICE_ENABLED false ist — nach
   * dem Sale die Konstante oben auf true setzen.
   */
  _syncAddToCartPrice() {
    const fillButton = (button) => {
      if (!button) return;

      let host = button.querySelector(".gdn-atc-price");
      if (!this._atcPriceEnabled()) {
        host?.remove();
        return;
      }

      const priceList = document.querySelector(
        ".product-meta__price-list-container"
      );

      const readPrice = (selector) => {
        const el = priceList?.querySelector(selector);
        if (!el || el.offsetParent === null) return "";

        const clone = el.cloneNode(true);
        clone.querySelectorAll(".visually-hidden").forEach((n) => n.remove());
        return clone.textContent.trim();
      };

      const current =
        readPrice(".price.price--highlight") ||
        readPrice(".price.price--large");
      const compare = readPrice(".price.price--compare");

      if (!current) {
        host?.remove();
        return;
      }

      if (!host) {
        host = document.createElement("span");
        host.className = "gdn-atc-price";
        const textEl = button.querySelector(".loader-button__text");
        (textEl || button).appendChild(host);
      }

      host.replaceChildren();

      const rule = document.createElement("span");
      rule.className = "gdn-atc-price__rule";
      rule.setAttribute("aria-hidden", "true");
      host.appendChild(rule);

      if (compare && compare !== current) {
        const compareEl = document.createElement("s");
        compareEl.className = "gdn-atc-price__compare";
        compareEl.textContent = compare;
        host.appendChild(compareEl);
      }

      const nowEl = document.createElement("span");
      nowEl.className = "gdn-atc-price__now";
      nowEl.textContent = current;
      host.appendChild(nowEl);
    };

    fillButton(document.querySelector("#AddToCart"));
    fillButton(document.querySelector("#StickyAddToCart"));
  }

  calculateDifference() {
    setTimeout(() => {
      this._syncAddToCartPrice();

      try {
        // Spezifisch den Preis-Container des Hauptprodukts finden
        const priceContainer = document.querySelector(
          ".product-meta__price-list-container"
        );
        if (!priceContainer) return;

        // Innerhalb des Containers nach den Preisen suchen
        const partPriceText = priceContainer.querySelector(
          ".price.price--compare"
        )?.innerText;
        const wholePriceText = priceContainer.querySelector(
          ".price.price--highlight"
        )?.innerText;

        if (!partPriceText || !wholePriceText) return;

        // Deutsches Zahlenformat korrekt parsen
        const parseGermanPrice = (text) => {
          return parseFloat(
            text
              .replace(/[^0-9.,]/g, "") // Nur Zahlen, Punkte und Kommas behalten
              .replace(/\./g, "") // ALLE Punkte entfernen (Tausendertrennzeichen)
              .replace(",", ".") // Komma durch Punkt ersetzen (Dezimaltrennzeichen)
          );
        };

        const partPrice = parseGermanPrice(partPriceText);
        const wholePrice = parseGermanPrice(wholePriceText);

        if (isNaN(partPrice) || isNaN(wholePrice)) return;

        const discountPercentage = Math.round(
          ((partPrice - wholePrice) / partPrice) * 100
        );

        const discountEl = document.querySelector(".discountPricePrecentage");
        if (discountEl) {
          const label =
            discountEl.dataset && discountEl.dataset.discountLabel
              ? discountEl.dataset.discountLabel.trim()
              : "";
          discountEl.innerText = label
            ? `${label} ${discountPercentage}%`
            : `${discountPercentage}%`;
        }
      } catch (error) {
        // Silently fail
      }
    }, 900);
  }

  observePriceChanges() {
    let previousPrice = document.querySelector(
      ".price.price--highlight .money"
    )?.innerText;

    // Statt setInterval verwenden wir MutationObserver
    const priceElement = document.querySelector(".price.price--highlight");
    if (!priceElement) return;

    const observer = new MutationObserver(() => {
      const currentPrice = document.querySelector(
        ".price.price--highlight .money"
      )?.innerText;
      if (currentPrice !== previousPrice) {
        previousPrice = currentPrice;
        this.calculateDifference();
      }
    });

    observer.observe(priceElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }
}

// Web Component registrieren
customElements.define("set-configurator", SetConfigurator);
